const pageSelector = "body.template-suffix-black-friday-2026";
const readyClass = "black-friday-2026-ready";
const initializeEventName = "black-friday-2026:initialize";
const initializedTabLists = new WeakSet();
const initializedSection7TabLists = new WeakSet();
const initializedSection7JoinButtons = new WeakSet();
const initializedSection7Tracking = new WeakSet();
const initializedProductLinkPages = new WeakSet();
const initializedSection7ReferralContainers = new WeakSet();
const initializedAnchorTabs = new WeakSet();
const couponImageSelector =
  '[handle="section1"] > [handle="container"] > [handle^="image-" i], ' +
  '[handle="section7"] [handle="container-free-in"] [handle^="image-" i]';
const initializedCouponImages = new WeakSet();
const discountProductRequests = new Map();
const section4AlignmentObservers = new WeakMap();
const section4AlignmentFrames = new WeakMap();
const section4SpacingObservers = new WeakMap();
const section2AlignmentFrames = new WeakMap();
const section2DesktopAlignmentFrames = new WeakMap();
const initializedSection2Images = new WeakSet();
const section3AlignmentObservers = new WeakMap();
const section3AlignmentFrames = new WeakMap();
const initializedSection3ProductScrollers = new WeakSet();
const section2And4MobileButtonPositions = new WeakMap();
const section5MobileButtonPositions = new WeakMap();
const initializedSection5ProductControls = new WeakSet();
const initializedSection7ProductScrollers = new WeakSet();
const initializedMobileQuickbuyButtons = new WeakSet();
const initializedMobileQuickbuyContainers = new WeakSet();
const mobileQuickbuyButtonRequests = new WeakMap();
const section1LayoutObservers = new WeakMap();
const section1LayoutFrames = new WeakMap();
const section6CountdownPositions = new WeakMap();
const backToTopButtonId = "black-friday-2026-back-to-top";
const section2MobileBatchSize = 8;
const productDiscountHandlePrefix = "container-";
const productCardDiscountHandlePrefix = "product-card-";
const discountProductScopeSelector =
  '[handle="section4"], ' +
  '[handle="section7"] ' +
  ':is([handle="container-free-in"], [handle="container-product-in"])';
let activeQuickbuyDiscountCode = "";
let pendingQuickbuyDiscountCode = "";
let discountEventsInitialized = false;
let couponToastTimer = null;
let stickyHeaderResizeObserver = null;
let section7AuthModulePromise = null;
let section7ReferralsDependenciesPromise = null;
let section7MemberPriceObserver = null;
let section7MemberPriceObserverTarget = null;
let section7MemberPriceFrame = null;
let mobileQuickbuyRequest = null;
let mobileQuickbuyIsOpen = false;
let mobileQuickbuyBodyOverflow = "";

function getDirectChildren(element, selector) {
  return Array.from(element.children).filter((child) =>
    child.matches(selector),
  );
}

async function resolveImportedAssetUrl(
  moduleScriptUrl,
  assetFileName,
  fallbackUrl,
) {
  const resolvedModuleScriptUrl = new URL(
    moduleScriptUrl,
    window.location.href,
  ).href;
  const resolvedFallbackUrl = new URL(fallbackUrl, window.location.href).href;

  try {
    const response = await fetch(resolvedModuleScriptUrl, {
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`Asset lookup failed with ${response.status}`);
    }

    const source = await response.text();
    const escapedAssetFileName = assetFileName.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&",
    );
    const importedAssetMatch = source.match(
      new RegExp(
        `["']((?:[^"']*/)?${escapedAssetFileName}(?:[?#][^"']*)?)["']`,
      ),
    );

    if (importedAssetMatch?.[1]) {
      return new URL(importedAssetMatch[1], resolvedModuleScriptUrl).href;
    }
  } catch (error) {
    console.warn("Black Friday asset dependency lookup error:", error);
  }

  return resolvedFallbackUrl;
}

async function resolveSection7RuntimeCoreUrl(configuredRuntimeCoreUrl) {
  const configuredUrl = new URL(
    configuredRuntimeCoreUrl,
    window.location.href,
  );
  const loadedRuntimeCoreUrls =
    typeof window.performance?.getEntriesByType === "function"
      ? window.performance
          .getEntriesByType("resource")
          .map((entry) => entry.name)
          .filter((resourceUrl) => {
            try {
              const loadedUrl = new URL(resourceUrl, window.location.href);
              return (
                loadedUrl.pathname === configuredUrl.pathname &&
                loadedUrl.search === configuredUrl.search
              );
            } catch {
              return false;
            }
          })
      : [];
  const loadedRuntimeCoreUrl =
    loadedRuntimeCoreUrls.find((resourceUrl) => {
      try {
        return new URL(resourceUrl).origin !== configuredUrl.origin;
      } catch {
        return false;
      }
    }) ||
    loadedRuntimeCoreUrls[0];

  if (loadedRuntimeCoreUrl) return loadedRuntimeCoreUrl;

  const vipHelperScript = document.querySelector(
    'script[src*="section.vip-helper.js"]',
  );

  if (vipHelperScript?.src) {
    return resolveImportedAssetUrl(
      vipHelperScript.src,
      "runtime-core.js",
      configuredUrl.href,
    );
  }

  return configuredUrl.href;
}

function getSection7AuthModule() {
  if (section7AuthModulePromise) return section7AuthModulePromise;

  const pageScript = document.querySelector(
    'script[src*="collection.black-friday-2026.js"][data-runtime-core-url]',
  );
  const configuredRuntimeCoreUrl = pageScript?.dataset.runtimeCoreUrl;

  if (!configuredRuntimeCoreUrl) {
    return Promise.reject(
      new Error("Black Friday membership runtime URL is unavailable."),
    );
  }

  section7AuthModulePromise = resolveSection7RuntimeCoreUrl(
    configuredRuntimeCoreUrl,
  )
    .then((runtimeCoreUrl) => import(runtimeCoreUrl))
    .catch((error) => {
      section7AuthModulePromise = null;
      throw error;
    });
  return section7AuthModulePromise;
}

function getSection7ReferralsDependencies() {
  if (section7ReferralsDependenciesPromise) {
    return section7ReferralsDependenciesPromise;
  }

  const pageScript = document.querySelector(
    'script[src*="collection.black-friday-2026.js"]' +
      "[data-client-url][data-login-layout-url]",
  );
  const clientUrl = pageScript?.dataset.clientUrl;
  const loginLayoutUrl = pageScript?.dataset.loginLayoutUrl;

  if (!clientUrl || !loginLayoutUrl) {
    return Promise.reject(
      new Error("Black Friday referrals resources are unavailable."),
    );
  }

  const resolvedLoginLayoutUrl = new URL(
    loginLayoutUrl,
    window.location.href,
  ).href;

  section7ReferralsDependenciesPromise = resolveImportedAssetUrl(
    resolvedLoginLayoutUrl,
    "client.js",
    clientUrl,
  )
    .then((resolvedClientUrl) =>
      Promise.all([
        import(resolvedClientUrl),
        import(resolvedLoginLayoutUrl),
      ]),
    )
    .catch((error) => {
      section7ReferralsDependenciesPromise = null;
      throw error;
    });

  return section7ReferralsDependenciesPromise;
}

function getProductContentContainer(productCard) {
  return getDirectChildren(
    productCard,
    '[handle="container"], [handle^="container-"]',
  )[0];
}

function getProductUrl(productCard) {
  const productUrlSource = productCard.querySelector(
    '[data-product-href], a[href*="/products/"]',
  );
  const productUrl =
    productCard.dataset.productUrl ||
    productUrlSource?.dataset.productHref ||
    productUrlSource?.getAttribute("href") ||
    "";

  if (!productUrl || productUrl === "#") return "";

  productCard.dataset.productUrl = productUrl;
  return productUrl;
}

function getProductLinkContext(target) {
  const targetElement =
    target instanceof Element ? target : target?.parentElement;
  const productCard = targetElement?.closest?.('[handle="product-card"]');
  if (
    !productCard?.closest(
      ':is([handle="section2"], [handle="section3"], [handle="section4"], [handle="section5"], [handle="section6"], [handle="section7"])',
    )
  ) {
    return null;
  }

  const productContent = getProductContentContainer(productCard);
  const media = productContent
    ? getDirectChildren(productContent, '[handle="container"]')[0]
    : null;
  const title = productContent?.querySelector('[handle="title"]');
  const linkTarget = [media, title]
    .filter(Boolean)
    .find((element) => element === targetElement || element.contains(targetElement));

  if (!linkTarget) return null;

  return {
    linkTarget,
    productCard,
    productUrl: getProductUrl(productCard),
  };
}

function getProductDiscountCode(productCard) {
  const section7FreeContainer = productCard.closest(
    '[handle="container-free-in"]',
  );
  const section7CouponHandle =
    section7FreeContainer
      ?.querySelector('[handle^="image-"]')
      ?.getAttribute("handle") || "";

  if (section7CouponHandle.startsWith("image-")) {
    return section7CouponHandle.slice("image-".length).trim();
  }

  const productContent = getProductContentContainer(productCard);
  const productContentHandle = productContent?.getAttribute("handle") || "";

  if (productContentHandle.startsWith(productDiscountHandlePrefix)) {
    return productContentHandle
      .slice(productDiscountHandlePrefix.length)
      .trim();
  }

  const productCardHandle = productCard.getAttribute("handle") || "";

  if (!productCardHandle.startsWith(productCardDiscountHandlePrefix)) {
    return "";
  }

  return productCardHandle
    .slice(productCardDiscountHandlePrefix.length)
    .trim();
}

async function applyQuickbuyDiscount(discountCode) {
  try {
    const response = await fetch(
      `/discount/${encodeURIComponent(discountCode)}`,
      { credentials: "same-origin" },
    );

    if (!response.ok) {
      throw new Error(`Discount request failed with ${response.status}`);
    }
  } catch (error) {
    console.error("Black Friday discount apply error:", error);
  }
}

function initializeDiscountEvents() {
  if (discountEventsInitialized) return;

  document.addEventListener(
    "click",
    async (event) => {
      const buyNowButton = event.target.closest?.("[data-buy-now]");
      const quickbuyRoot = buyNowButton?.closest?.(
        "#quickbuy-modal-root, #product-button-mobile-container, " +
          "#black-friday-2026-mobile-quickbuy-container",
      );
      const discountCode = activeQuickbuyDiscountCode;

      if (!buyNowButton || !quickbuyRoot || !discountCode) return;

      const addToCartForm = quickbuyRoot.querySelector(
        'form[action$="/cart/add"]',
      );
      if (!addToCartForm) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      activeQuickbuyDiscountCode = "";
      pendingQuickbuyDiscountCode = "";

      const formData = new FormData(addToCartForm);
      const quantityInput = quickbuyRoot.querySelector(
        '[name="quantity"], #quantity',
      );
      if (quantityInput?.value) {
        formData.set("quantity", quantityInput.value);
      }

      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || "/"}cart/add.js`,
          { method: "POST", body: formData },
        );

        if (!response.ok) {
          throw new Error(`Buy now add-to-cart failed with ${response.status}`);
        }

        await response.json().catch(() => null);
        await applyQuickbuyDiscount(discountCode);
        window.location.assign("/checkout");
      } catch (error) {
        console.error("Black Friday buy now error:", error);
      }
    },
    true,
  );

  document.addEventListener("click", (event) => {
    const quickbuyButton = event.target.closest?.(
      '[data-product-quickbuy="true"]',
    );

    if (quickbuyButton) {
      pendingQuickbuyDiscountCode = "";
      activeQuickbuyDiscountCode = quickbuyButton.closest(
        discountProductScopeSelector,
      )
        ? quickbuyButton.dataset.discountCode || ""
        : "";
      return;
    }

    if (
      event.target.closest?.(
        '#quickbuy-modal-root [aria-label="Close quick buy"]',
      )
    ) {
      activeQuickbuyDiscountCode = "";
      pendingQuickbuyDiscountCode = "";
    }
  });

  document.addEventListener("submit", (event) => {
    const form = event.target;
    const isQuickbuyForm = form.closest?.(
      "#quickbuy-modal-root, #product-button-mobile-container, " +
        "#black-friday-2026-mobile-quickbuy-container",
    );

    if (!activeQuickbuyDiscountCode || !isQuickbuyForm) return;

    pendingQuickbuyDiscountCode = activeQuickbuyDiscountCode;
  });

  document.documentElement.addEventListener("theme:cartchanged", () => {
    const discountCode = pendingQuickbuyDiscountCode;

    pendingQuickbuyDiscountCode = "";
    activeQuickbuyDiscountCode = "";
    if (discountCode) applyQuickbuyDiscount(discountCode);
  });

  discountEventsInitialized = true;
}

function initializeProductDiscountCodes(root = document) {
  const closestScope = root.closest?.(discountProductScopeSelector);
  const scopes = root.matches?.(discountProductScopeSelector)
    ? [root]
    : closestScope
      ? [closestScope]
      : root.querySelectorAll?.(discountProductScopeSelector) || [];

  scopes.forEach((scope) => {
    scope
      .querySelectorAll(
        '[handle="product-card"], [handle^="product-card-"]',
      )
      .forEach((productCard) => {
        const discountCode = getProductDiscountCode(productCard);
        const button = productCard.querySelector(
          '[handle="product-button"][data-product-quickbuy="true"]',
        );

        if (!button) return;

        if (discountCode) {
          button.dataset.discountCode = discountCode;
        } else {
          delete button.dataset.discountCode;
        }
      });
  });

  initializeDiscountEvents();
}

function getMobileQuickbuyContainer() {
  let container = document.getElementById(
    "black-friday-2026-mobile-quickbuy-container",
  );

  if (!container) {
    container = document.createElement("div");
    container.id = "black-friday-2026-mobile-quickbuy-container";
    container.className = "product-button-mobile-container";
    document.body.append(container);
  }

  if (!initializedMobileQuickbuyContainers.has(container)) {
    container.addEventListener(
      "click",
      (event) => {
        const closeTrigger = event.target.closest?.(
          ".product-column-float-mobile .close-detail, " +
            ".product-column-float-mobile .overlay",
        );
        if (!closeTrigger) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        closeMobileQuickbuy(container);
      },
      true,
    );
    initializedMobileQuickbuyContainers.add(container);
  }

  return container;
}

function closeMobileQuickbuy(container) {
  mobileQuickbuyRequest?.abort();
  mobileQuickbuyRequest = null;

  const jquery = window.jQuery || window.$;
  if (typeof jquery === "function") {
    jquery(container).find(".product-form").trigger("unload-product-form");
  }

  container.replaceChildren();
  if (mobileQuickbuyIsOpen) {
    document.body.style.overflow = mobileQuickbuyBodyOverflow;
  }
  mobileQuickbuyIsOpen = false;
  mobileQuickbuyBodyOverflow = "";
  activeQuickbuyDiscountCode = "";
  pendingQuickbuyDiscountCode = "";
}

async function openMobileQuickbuy(button) {
  const productUrl = button.dataset.productHref;
  if (!productUrl) return;

  const container = getMobileQuickbuyContainer();
  closeMobileQuickbuy(container);
  pendingQuickbuyDiscountCode = "";
  activeQuickbuyDiscountCode = button.dataset.discountCode || "";

  const request = new AbortController();
  mobileQuickbuyRequest = request;
  mobileQuickbuyButtonRequests.set(button, request);
  button.setAttribute("aria-busy", "true");

  try {
    const response = await fetch(productUrl, {
      credentials: "same-origin",
      signal: request.signal,
    });
    if (!response.ok) {
      throw new Error(`Quick buy request failed with ${response.status}`);
    }

    const responseDocument = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );
    const quickbuyContent = responseDocument.querySelector(
      ".quickbuy-content-mobile",
    );
    if (!quickbuyContent) {
      throw new Error("Mobile quick buy content is unavailable.");
    }
    if (mobileQuickbuyRequest !== request) return;

    container.replaceChildren(
      ...Array.from(quickbuyContent.childNodes, (node) =>
        document.importNode(node, true),
      ),
    );
    const quickbuy = container.querySelector(
      ".product-column-float-mobile",
    );
    if (!quickbuy) {
      throw new Error("Mobile quick buy dialog is unavailable.");
    }

    mobileQuickbuyBodyOverflow = document.body.style.overflow;
    mobileQuickbuyIsOpen = true;
    quickbuy.style.display = "block";
    document.body.style.overflow = "hidden";

    const jquery = window.jQuery || window.$;
    if (typeof jquery !== "function") {
      throw new Error("Mobile quick buy dependencies are unavailable.");
    }

    const $container = jquery(container);
    const $productForms = $container.find(".product-form");
    window.theme?.initProductSlick?.($container);
    window.theme?.initProductGallery?.($container);
    $productForms
      .attr("data-is-quick-buy-form", "true")
      .trigger("load-product-form")
      .on("variantChanged", () => $container.trigger("changedsize"));
    $container
      .find(".product-column-float-mobile .product-form")
      .trigger("load-product-form");
    window.theme?.ProductBlockManager?.loadImages?.($container);
    window.theme?.OptionManager?.getProductData?.($productForms);
  } catch (error) {
    if (error?.name !== "AbortError") {
      if (mobileQuickbuyRequest === request) {
        closeMobileQuickbuy(container);
      }
      console.error("Black Friday mobile quick buy error:", error);
    }
  } finally {
    if (mobileQuickbuyRequest === request) {
      mobileQuickbuyRequest = null;
    }
    if (mobileQuickbuyButtonRequests.get(button) === request) {
      mobileQuickbuyButtonRequests.delete(button);
      button.removeAttribute("aria-busy");
    }
  }
}

function initializeMobileQuickbuyButtons(root = document) {
  const sectionSelector =
    ':is([handle="section2"], [handle="section3"], [handle="section4"], ' +
    '[handle="section5"], [handle="section6"], [handle="section7"])';
  const closestSection = root.closest?.(sectionSelector);
  const sections = root.matches?.(sectionSelector)
    ? [root]
    : closestSection
      ? [closestSection]
      : root.querySelectorAll?.(sectionSelector) || [];

  sections.forEach((section) => {
    const buttonSelector = section.matches('[handle="section7"]')
      ? ':is([handle="container-free-in"], ' +
        '[handle="container-product-in"]) ' +
        '[handle="product-button"][data-product-href]'
      : '[handle="product-button"][data-product-href]';

    section
      .querySelectorAll(buttonSelector)
      .forEach((button) => {
        button.dataset.productQuickbuy = "true";
        button.dataset.href = button.dataset.productHref;
        delete button.dataset.blackFridaySection7Quickbuy;

        if (initializedMobileQuickbuyButtons.has(button)) return;

        button.addEventListener("click", (event) => {
          if (!window.matchMedia("(max-width: 767px)").matches) return;

          event.preventDefault();
          event.stopPropagation();
          openMobileQuickbuy(button);
        });
        initializedMobileQuickbuyButtons.add(button);
      });
  });

  const previousHandler =
    document.__blackFriday2026Section7QuickbuyHandler;

  if (previousHandler) {
    document.removeEventListener("click", previousHandler, true);
    delete document.__blackFriday2026Section7QuickbuyHandler;
  }
}

function initializeBackToTop() {
  if (document.getElementById(backToTopButtonId)) return;

  const button = document.createElement("button");

  button.id = backToTopButtonId;
  button.className = "black-friday-2026-back-to-top";
  button.type = "button";
  button.setAttribute("aria-label", "Back to top");
  button.title = "Back to top";
  const updateVisibility = () => {
    const isVisible = window.scrollY > window.innerHeight * 1.2;
    button.classList.toggle("is-visible", isVisible);
    button.setAttribute("aria-hidden", String(!isVisible));
    button.tabIndex = isVisible ? 0 : -1;
  };

  button.addEventListener("click", () => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  });

  document.body.append(button);
  updateVisibility();
  window.addEventListener("scroll", updateVisibility, { passive: true });
  window.addEventListener("resize", updateVisibility, { passive: true });
}

function getConfiguredStyleValue(elements, propertyName) {
  for (const element of elements.filter(Boolean)) {
    const inlineValue = element.style.getPropertyValue(propertyName).trim();
    const value =
      inlineValue ||
      window.getComputedStyle(element).getPropertyValue(propertyName).trim();

    if (value && value.toLowerCase() !== "auto") return value;
  }

  return "";
}

function applyConfiguredMobileProductDimensions(slide, productCard) {
  const productContainer = getProductContentContainer(productCard);
  const configuredElements = [productCard, productContainer];
  const dimensions = {
    width: getConfiguredStyleValue(configuredElements, "--w-mb"),
    height: getConfiguredStyleValue(configuredElements, "--h-mb"),
    "aspect-ratio": getConfiguredStyleValue(
      configuredElements,
      "--aspect-ratio-mb",
    ),
  };

  Object.entries(dimensions).forEach(([dimension, value]) => {
    const propertyName = `--black-friday-2026-mobile-product-${dimension}`;

    if (value) {
      slide.style.setProperty(propertyName, value);
    } else {
      slide.style.removeProperty(propertyName);
    }
  });
}

function applyConfiguredMobileProductGap(productTrack, productContainer) {
  const configuredGap = getConfiguredStyleValue(
    [productContainer],
    "--gap-mb",
  );
  const numericGap = Number.parseFloat(configuredGap);

  if (configuredGap && Number.isFinite(numericGap) && numericGap > 0) {
    productTrack.style.setProperty(
      "--black-friday-2026-mobile-product-gap",
      configuredGap,
    );
  } else {
    productTrack.style.removeProperty(
      "--black-friday-2026-mobile-product-gap",
    );
  }
}

function applyConfiguredSection3MobileImageDimensions(media, image) {
  const dimensions = {
    width: getConfiguredStyleValue([image], "--w-mb"),
    height: getConfiguredStyleValue([image], "--h-mb"),
    "aspect-ratio": getConfiguredStyleValue([image], "--aspect-ratio-mb"),
  };

  Object.entries(dimensions).forEach(([dimension, value]) => {
    const propertyName = `--black-friday-2026-section3-image-${dimension}`;

    if (value) {
      media.style.setProperty(propertyName, value);
    } else {
      media.style.removeProperty(propertyName);
    }
  });
}

function getOrCreateCartButtonMount(media) {
  let buttonMount = getDirectChildren(
    media,
    ":is(.black-friday-2026-cart-button-mount, " +
      ".black-friday-2026-section3-cart-button-mount)",
  )[0];

  if (!buttonMount) {
    buttonMount = document.createElement("div");
    media.appendChild(buttonMount);
  }

  buttonMount.classList.remove(
    "black-friday-2026-section3-cart-button-mount",
  );
  buttonMount.classList.add("black-friday-2026-cart-button-mount");

  return buttonMount;
}

function updateSection2And4MobileCartButtons(root = document) {
  const sectionSelector = ':is([handle="section2"], [handle="section4"])';
  const closestSection = root.closest?.(sectionSelector);
  const sections = root.matches?.(sectionSelector)
    ? [root]
    : closestSection
      ? [closestSection]
      : root.querySelectorAll?.(sectionSelector) || [];
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  sections.forEach((section) => {
    section.querySelectorAll('[handle="product-card"]').forEach((productCard) => {
      const productContent = getProductContentContainer(productCard);
      const media = productContent
        ? getDirectChildren(productContent, '[handle="container"]')[0]
        : null;
      const image = media
        ? getDirectChildren(media, '[handle="image"]')[0]
        : null;
      const button = productContent?.querySelector('[handle="product-button"]');

      if (!media || !image || !button) return;

      media.classList.add(
        "black-friday-2026-section2-section4-mobile-cart-media",
      );

      if (!section2And4MobileButtonPositions.has(button)) {
        section2And4MobileButtonPositions.set(button, {
          parent: button.parentElement,
          nextSibling: button.nextSibling,
        });
      }

      if (isMobile) {
        if (!button.getAttribute("aria-label")) {
          button.setAttribute(
            "aria-label",
            button.textContent.trim() || "Add to cart",
          );
        }
        button.classList.add("black-friday-2026-section3-cart-button");
        media.appendChild(button);
        return;
      }

      const originalPosition = section2And4MobileButtonPositions.get(button);
      if (originalPosition?.parent?.isConnected) {
        const nextSibling =
          originalPosition.nextSibling?.parentElement === originalPosition.parent
            ? originalPosition.nextSibling
            : null;
        originalPosition.parent.insertBefore(button, nextSibling);
      }
      button.classList.remove("black-friday-2026-section3-cart-button");
    });
  });
}

function updateSection3MobileCards(root = document) {
  const productCards =
    root.matches?.('[handle="product-card"]') &&
    root.closest('[handle="section3"]')
      ? [root]
      : root.querySelectorAll?.(
          '[handle="section3"] [handle="product-card"]',
        ) || [];

  productCards.forEach((productCard) => {
    const productContent = getProductContentContainer(productCard);
    const media = productContent
      ? getDirectChildren(productContent, '[handle="container"]')[0]
      : null;
    const image = media
      ? getDirectChildren(media, '[handle="image"]')[0]
      : null;
    const button = productContent?.querySelector('[handle="product-button"]');

    if (!productContent || !media || !image || !button) return;

    const buttonMount = getOrCreateCartButtonMount(media);

    applyConfiguredSection3MobileImageDimensions(media, image);

    productCard.classList.add("black-friday-2026-section3-mobile-card");
    productContent.classList.add(
      "black-friday-2026-section3-mobile-card-content",
    );
    media.classList.add("black-friday-2026-section3-mobile-card-media");

    if (!button.getAttribute("aria-label")) {
      button.setAttribute(
        "aria-label",
        button.textContent.trim() || "Add to cart",
      );
    }
    button.classList.add("black-friday-2026-section3-cart-button");
    buttonMount.appendChild(button);
  });
}

function applyConfiguredSection5MobileImageDimensions(media, image) {
  const dimensions = {
    width: getConfiguredStyleValue([image], "--w-mb"),
    height: getConfiguredStyleValue([image], "--h-mb"),
    "aspect-ratio": getConfiguredStyleValue([image], "--aspect-ratio-mb"),
  };

  Object.entries(dimensions).forEach(([dimension, value]) => {
    const propertyName = `--black-friday-2026-section5-image-${dimension}`;

    if (value) {
      media.style.setProperty(propertyName, value);
    } else {
      media.style.removeProperty(propertyName);
    }
  });
}

function updateSection5MobileCards(root = document) {
  const sectionSelector =
    ':is([handle="section5"], [handle="section7"])';
  const section = root.matches?.(sectionSelector)
    ? root
    : root.closest?.(sectionSelector);
  const productCards = section
    ? section.querySelectorAll('[handle="product-card"]')
    : root.querySelectorAll?.(
        `${sectionSelector} [handle="product-card"]`,
      ) || [];
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  productCards.forEach((productCard) => {
    const keepImageButton =
      isMobile || Boolean(productCard.closest('[handle="section5"]'));
    const productContent = getProductContentContainer(productCard);
    const media = productContent
      ? getDirectChildren(productContent, '[handle="container"]')[0]
      : null;
    const image = media
      ? getDirectChildren(media, '[handle="image"]')[0]
      : null;
    const button = productContent?.querySelector('[handle="product-button"]');

    if (!productContent || !media || !image || !button) return;

    applyConfiguredSection5MobileImageDimensions(media, image);

    productCard.classList.add("black-friday-2026-section5-mobile-card");
    productContent.classList.add(
      "black-friday-2026-section5-mobile-card-content",
    );
    media.classList.add("black-friday-2026-section5-mobile-card-media");

    if (!section5MobileButtonPositions.has(button)) {
      section5MobileButtonPositions.set(button, {
        parent: button.parentElement,
        nextSibling: button.nextSibling,
      });
    }

    if (keepImageButton) {
      if (!button.getAttribute("aria-label")) {
        button.setAttribute(
          "aria-label",
          button.textContent.trim() || "Add to cart",
        );
      }
      button.classList.add("black-friday-2026-section5-cart-button");
      getOrCreateCartButtonMount(media).appendChild(button);
      return;
    }

    const originalPosition = section5MobileButtonPositions.get(button);
    if (originalPosition?.parent?.isConnected) {
      const nextSibling =
        originalPosition.nextSibling?.parentElement === originalPosition.parent
          ? originalPosition.nextSibling
          : null;
      originalPosition.parent.insertBefore(button, nextSibling);
    }
    button.classList.remove("black-friday-2026-section5-cart-button");
  });
}

function updateSection6MobileCartButtons(root = document) {
  const section = root.matches?.('[handle="section6"]')
    ? root
    : root.closest?.('[handle="section6"]');
  const productCards = section
    ? section.querySelectorAll('[handle="product-card"]')
    : root.querySelectorAll?.(
        '[handle="section6"] [handle="product-card"]',
      ) || [];

  productCards.forEach((productCard) => {
    const productContent = getProductContentContainer(productCard);
    const media = productContent
      ? getDirectChildren(productContent, '[handle="container"]')[0]
      : null;
    const button = productContent?.querySelector('[handle="product-button"]');

    if (!productContent || !media || !button) return;

    media.classList.add(
      "black-friday-2026-section6-mobile-cart-media",
    );

    if (!button.getAttribute("aria-label")) {
      button.setAttribute(
        "aria-label",
        button.textContent.trim() || "Add to cart",
      );
    }
    button.classList.add("black-friday-2026-section5-cart-button");
    getOrCreateCartButtonMount(media).appendChild(button);
  });
}

function updateSection6CountdownLayout(root = document) {
  const sectionSelector = '[handle="section6"]';
  const closestSection = root.closest?.(sectionSelector);
  const sections = root.matches?.(sectionSelector)
    ? [root]
    : closestSection
      ? [closestSection]
      : root.querySelectorAll?.(sectionSelector) || [];
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  sections.forEach((section) => {
    const container = getDirectChildren(section, '[handle="container"]')[0];
    const countdown = section.querySelector("[data-countdown]");

    if (!container || !countdown) return;

    if (!section6CountdownPositions.has(countdown)) {
      section6CountdownPositions.set(countdown, {
        parent: countdown.parentElement,
        nextSibling: countdown.nextSibling,
      });
    }

    if (isMobile) {
      countdown.classList.add(
        "black-friday-2026-section6-mobile-countdown",
      );
      container.insertAdjacentElement("afterend", countdown);
      return;
    }

    const originalPosition = section6CountdownPositions.get(countdown);
    if (originalPosition?.parent?.isConnected) {
      const nextSibling =
        originalPosition.nextSibling?.parentElement === originalPosition.parent
          ? originalPosition.nextSibling
          : null;
      originalPosition.parent.insertBefore(countdown, nextSibling);
    }
    countdown.classList.remove(
      "black-friday-2026-section6-mobile-countdown",
    );
  });
}

function getProductJsonUrl(productCard) {
  const productLink = productCard.querySelector(
    '[handle="product-button"][data-product-href], [handle="product-button"][href], a[href*="/products/"]',
  );
  const productHref =
    productLink?.dataset.productHref || productLink?.getAttribute("href");
  if (!productHref) return "";

  const productUrl = new URL(productHref, window.location.origin);
  productUrl.hash = "";
  productUrl.search = "";
  if (!productUrl.pathname.endsWith(".js")) {
    productUrl.pathname = `${productUrl.pathname.replace(/\/$/, "")}.js`;
  }

  return productUrl.toString();
}

function loadDiscountProduct(productUrl) {
  if (!discountProductRequests.has(productUrl)) {
    const request = fetch(productUrl, {
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    discountProductRequests.set(productUrl, request);
  }

  return discountProductRequests.get(productUrl);
}

function calculateDiscountPercentage(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const pricedVariants = variants
    .filter((variant) => Number(variant.price) > 0)
    .sort((first, second) => Number(first.price) - Number(second.price));
  const variant =
    pricedVariants[0] ||
    [...variants].sort(
      (first, second) => Number(first.price) - Number(second.price),
    )[0];
  const price = Number(variant?.price);
  const compareAtPrice = Number(variant?.compare_at_price);

  if (
    !Number.isFinite(price) ||
    !Number.isFinite(compareAtPrice) ||
    compareAtPrice <= 0 ||
    compareAtPrice <= price
  ) {
    return 0;
  }

  return ((compareAtPrice - price) / compareAtPrice) * 100;
}

function formatDiscountPercentage(discountPercentage) {
  return Math.round(discountPercentage).toString();
}

function getLowestProductSalePrice(product) {
  const positivePrices = (Array.isArray(product?.variants)
    ? product.variants
    : []
  )
    .map((variant) => Number(variant.price))
    .filter((price) => Number.isFinite(price) && price > 0);

  return positivePrices.length ? Math.min(...positivePrices) : 0;
}

function getDisplayedProductSalePrice(productCard) {
  const productContent = getProductContentContainer(productCard);
  const renderedPriceContainer = productContent
    ? getDirectChildren(productContent, '[handle="container"]').find(
        (container) => /\$\s*\d/.test(container.textContent),
      )
    : null;
  const salePrice =
    productCard.querySelector('[handle="product-sale-price"]') ||
    renderedPriceContainer ||
    (productContent
      ? getSection4ProductSlots(productContent)?.price
      : null);
  const numericPrice = salePrice?.textContent
    ?.replace(/,/g, "")
    .match(/\d+(?:\.\d+)?/i)?.[0];

  return numericPrice ? Math.round(Number(numericPrice) * 100) : 0;
}

function getSection7MemberPriceImage(productCard) {
  if (
    !productCard.closest(
      '[handle="section7"] :is([handle="container-product-out"], [handle="container-product-in"])',
    )
  ) {
    return null;
  }

  const directImage = getDirectChildren(productCard, '[handle="image"]')[0];
  if (directImage) return directImage;

  const productContents = getDirectChildren(
    productCard,
    '[handle^="container"]',
  );

  for (const productContent of productContents) {
    const memberPriceImage = getDirectChildren(
      productContent,
      '[handle="image"]',
    )[0];
    if (memberPriceImage) return memberPriceImage;
  }

  return null;
}

function updateSection7MemberPriceImage(productCard, product) {
  const memberPriceImage = getSection7MemberPriceImage(productCard);
  if (!memberPriceImage) return;

  const salePrice =
    getLowestProductSalePrice(product) ||
    getDisplayedProductSalePrice(productCard);
  memberPriceImage
    .querySelector(":scope > .black-friday-2026-section7-member-price")
    ?.remove();

  if (!salePrice) {
    delete memberPriceImage.dataset.memberPrice;
    memberPriceImage.classList.remove(
      "black-friday-2026-section7-member-price-image",
    );
    return;
  }

  const discountCents = Math.floor(salePrice / 5);
  const memberPriceCents = salePrice - discountCents;
  memberPriceImage.dataset.memberPrice = `$${(
    memberPriceCents / 100
  ).toFixed(2)}`;
  memberPriceImage.classList.add(
    "black-friday-2026-section7-member-price-image",
  );
}

function getSection4CouponPriceImage(productCard) {
  if (!productCard.closest('[handle="section4"]')) return null;

  const productContent = getProductContentContainer(productCard);
  return productContent
    ? getDirectChildren(productContent, '[handle="image"]')[0] || null
    : null;
}

function updateSection4CouponPriceImage(productCard, product) {
  const couponPriceImage = getSection4CouponPriceImage(productCard);
  if (!couponPriceImage) return;

  const salePrice =
    getLowestProductSalePrice(product) ||
    getDisplayedProductSalePrice(productCard);

  if (!salePrice) {
    delete couponPriceImage.dataset.couponPrice;
    couponPriceImage.classList.remove(
      "black-friday-2026-section4-coupon-price-image",
    );
    return;
  }

  const discountCents = Math.floor((salePrice * 30) / 100);
  const couponPriceCents = salePrice - discountCents;
  couponPriceImage.dataset.couponPrice = `$${(
    couponPriceCents / 100
  ).toFixed(2)}`;
  couponPriceImage.classList.add(
    "black-friday-2026-section4-coupon-price-image",
  );
}

function initializeSection7MemberPrices(root = document) {
  const section7Selector = '[handle="section7"]';
  const section7 = root.matches?.(section7Selector)
    ? root
    : root.closest?.(section7Selector) ||
      root.querySelector?.(section7Selector);
  if (!section7) return;

  section7
    .querySelectorAll(
      ':is([handle="container-product-out"], [handle="container-product-in"]) [handle="product-card"]',
    )
    .forEach((productCard) => updateSection7MemberPriceImage(productCard));
  initializeProductLinks(section7);
  initializeSection7JoinButtons(section7);
}

function initializeProductLinks(root = document) {
  const productCards = root.matches?.('[handle="product-card"]')
    ? [root]
    : root.querySelectorAll?.(
        ':is([handle="section2"], [handle="section3"], [handle="section4"], [handle="section5"], [handle="section6"], [handle="section7"]) [handle="product-card"], [handle="product-card"]',
      ) || [];

  productCards.forEach((productCard) => {
    if (
      !productCard.closest(
        ':is([handle="section2"], [handle="section3"], [handle="section4"], [handle="section5"], [handle="section6"], [handle="section7"])',
      )
    ) {
      return;
    }

    const productUrl = getProductUrl(productCard);
    const productContent = getProductContentContainer(productCard);
    const media = productContent
      ? getDirectChildren(productContent, '[handle="container"]')[0]
      : null;
    const title = productContent?.querySelector('[handle="title"]');
    if (!productUrl || !media) return;

    [media, title].filter(Boolean).forEach((linkTarget) => {
      linkTarget.classList.add("black-friday-2026-product-link");
      linkTarget.setAttribute("role", "link");
      linkTarget.setAttribute("tabindex", "0");
    });
  });

  const page = document.querySelector(pageSelector);
  if (!page || initializedProductLinkPages.has(page)) return;

  const prepareLinkTarget = (event) => {
    const context = getProductLinkContext(event.target);
    if (!context?.productUrl) return null;

    context.linkTarget.classList.add("black-friday-2026-product-link");
    context.linkTarget.setAttribute("role", "link");
    context.linkTarget.setAttribute("tabindex", "0");
    return context;
  };

  const navigateToProduct = (event) => {
    const context = prepareLinkTarget(event);
    if (!context) return;

    const nestedInteractiveElement = event.target.closest?.(
      'a, button, input, select, textarea, [handle="product-button"]',
    );
    if (
      nestedInteractiveElement &&
      nestedInteractiveElement !== context.linkTarget
    ) {
      return;
    }

    event.preventDefault();
    window.location.assign(context.productUrl);
  };

  page.addEventListener("pointerdown", prepareLinkTarget, true);
  page.addEventListener("click", navigateToProduct, true);
  page.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Enter") return;
      navigateToProduct(event);
    },
    true,
  );
  initializedProductLinkPages.add(page);
}

function scheduleSection7MemberPrices() {
  if (section7MemberPriceFrame) {
    window.cancelAnimationFrame(section7MemberPriceFrame);
  }

  section7MemberPriceFrame = window.requestAnimationFrame(() => {
    section7MemberPriceFrame = null;
    initializeSection7MemberPrices();
  });
}

function observeSection7MemberPrices(page) {
  const section7 = page.querySelector('[handle="section7"]');
  if (!section7 || section7MemberPriceObserverTarget === section7) return;

  section7MemberPriceObserver?.disconnect();

  section7MemberPriceObserver = new MutationObserver(
    scheduleSection7MemberPrices,
  );
  section7MemberPriceObserver.observe(section7, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  section7MemberPriceObserverTarget = section7;
}

function createDiscountBadge(discountPercentage) {
  const badge = document.createElement("div");
  const label = document.createElement("span");
  const discountText = `- ${formatDiscountPercentage(discountPercentage)}%`;

  badge.className = "black-friday-2026-discount-badge";
  badge.setAttribute("aria-label", discountText);
  label.className = "black-friday-2026-discount-badge__value";
  label.textContent = discountText;
  badge.append(label);

  return badge;
}

function updateSection6DiscountImage(productCard, discountPercentage) {
  if (!productCard.closest('[handle="section6"]')) return;

  const discountImage = productCard.querySelector(
    ':scope > [handle="container"] > .black-friday-2026-section4-product-price > [handle="image"]',
  );
  if (!discountImage) return;

  let discountValue = discountImage.querySelector(
    ":scope > .black-friday-2026-section6-discount-value",
  );

  if (discountPercentage <= 0) {
    discountValue?.remove();
    discountImage.classList.remove(
      "black-friday-2026-section6-discount-image",
    );
    discountImage.classList.add(
      "black-friday-2026-section6-discount-image--hidden",
    );
    return;
  }

  if (!discountValue) {
    discountValue = document.createElement("span");
    discountValue.className =
      "black-friday-2026-section6-discount-value";
    discountImage.append(discountValue);
  }

  discountImage.classList.add(
    "black-friday-2026-section6-discount-image",
  );
  discountImage.classList.remove(
    "black-friday-2026-section6-discount-image--hidden",
  );
  discountValue.textContent = `- ${formatDiscountPercentage(discountPercentage)}%`;
}

function initializeDiscountBadges(root = document) {
  const productCards = root.matches?.('[handle="product-card"]')
    ? [root]
    : root.querySelectorAll?.('[handle="product-card"]') || [];

  productCards.forEach(async (productCard) => {
    updateSection4CouponPriceImage(productCard);
    updateSection7MemberPriceImage(productCard);

    const productUrl = getProductJsonUrl(productCard);
    if (!productUrl) return;

    const product = await loadDiscountProduct(productUrl);
    if (!product || !productCard.isConnected) return;

    const discountPercentage = calculateDiscountPercentage(product);
    updateSection6DiscountImage(productCard, discountPercentage);
    updateSection4CouponPriceImage(productCard, product);
    updateSection7MemberPriceImage(productCard, product);
    const currentBadge = productCard.querySelector(
      ":scope > .black-friday-2026-discount-badge",
    );

    if (productCard.closest('[handle="section6"]')) {
      currentBadge?.remove();
      return;
    }

    if (discountPercentage <= 0) {
      currentBadge?.remove();
      return;
    }

    currentBadge?.remove();
    productCard.prepend(createDiscountBadge(discountPercentage));
  });
}

function initializeProductGrid(product) {
  const cards = getDirectChildren(product, "[data-embla-slide]");
  const hasMultipleRows = cards.length > 4;

  product.classList.toggle("has-multiple-product-rows", hasMultipleRows);
  product.classList.toggle("is-product-scrollable", cards.length > 8);

  cards.forEach((card, index) => {
    if (!hasMultipleRows) {
      card.style.removeProperty("--black-friday-product-column");
      card.style.removeProperty("--black-friday-product-row");
      return;
    }

    const pageIndex = Math.floor(index / 8);
    const positionInPage = index % 8;
    const row = Math.floor(positionInPage / 4) + 1;
    const column = pageIndex * 4 + (positionInPage % 4) + 1;

    card.style.setProperty("--black-friday-product-column", column);
    card.style.setProperty("--black-friday-product-row", row);
  });
}

function initializeSection3ProductScroller(productTrack) {
  productTrack.classList.add("black-friday-2026-section3-drag-scroller");

  if (
    initializedSection3ProductScrollers.has(productTrack) ||
    !("PointerEvent" in window)
  ) {
    return;
  }

  const desktopMedia = window.matchMedia("(min-width: 768px)");
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let gestureDirection = "";
  let didDrag = false;
  let suppressClickAfterDrag = false;
  let suppressClickTimer = null;

  const finishGesture = (event) => {
    if (pointerId !== event.pointerId) return;

    if (didDrag) {
      suppressClickAfterDrag = true;
      window.clearTimeout(suppressClickTimer);
      suppressClickTimer = window.setTimeout(() => {
        suppressClickAfterDrag = false;
      }, 80);
    }
    if (productTrack.hasPointerCapture?.(pointerId)) {
      productTrack.releasePointerCapture(pointerId);
    }

    pointerId = null;
    gestureDirection = "";
    didDrag = false;
    productTrack.classList.remove("is-dragging");
  };

  productTrack.addEventListener(
    "pointerdown",
    (event) => {
      if (
        !desktopMedia.matches ||
        !event.isPrimary ||
        event.button !== 0 ||
        event.target.closest?.(".black-friday-2026-product-link") ||
        productTrack.scrollWidth <= productTrack.clientWidth + 1
      ) {
        return;
      }

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = productTrack.scrollLeft;
      gestureDirection = "";
      didDrag = false;
    },
    true,
  );
  productTrack.addEventListener(
    "pointermove",
    (event) => {
      if (pointerId !== event.pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      if (!gestureDirection) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
        if (Math.abs(deltaX) < Math.abs(deltaY) * 0.85) return;
        gestureDirection = "horizontal";
      }

      if (gestureDirection !== "horizontal") return;

      event.preventDefault();
      event.stopPropagation();
      if (!didDrag) {
        didDrag = true;
        productTrack.classList.add("is-dragging");
        productTrack.setPointerCapture?.(pointerId);
      }
      productTrack.scrollLeft = startScrollLeft - deltaX;
    },
    { passive: false, capture: true },
  );
  productTrack.addEventListener("pointerup", finishGesture, true);
  productTrack.addEventListener("pointercancel", finishGesture, true);
  productTrack.addEventListener("lostpointercapture", finishGesture);
  productTrack.addEventListener(
    "dragstart",
    (event) => {
      if (desktopMedia.matches) event.preventDefault();
    },
    true,
  );
  productTrack.addEventListener(
    "click",
    (event) => {
      if (event.target.closest?.(".black-friday-2026-product-link")) return;
      if (!suppressClickAfterDrag) return;
      suppressClickAfterDrag = false;
      window.clearTimeout(suppressClickTimer);
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true,
  );

  initializedSection3ProductScrollers.add(productTrack);
}

function getStickyHeaderElements() {
  const selectors = [
    ".section-top-countdown-bar",
    ".section-announcement-bar",
    ".section-header",
  ];

  return Array.from(
    new Set(
      selectors
        .map((selector) => document.querySelector(selector))
        .filter(Boolean)
        .map((element) =>
          element.matches(".section-header")
            ? element
            : element.closest(".shopify-section") || element,
        ),
    ),
  );
}

function updateStickyAnchorOffset() {
  const stickyHeaderBottom = getStickyHeaderElements().reduce(
    (currentBottom, element) => {
      const styles = window.getComputedStyle(element);
      if (
        styles.display === "none" ||
        (styles.position !== "sticky" && styles.position !== "fixed")
      ) {
        return currentBottom;
      }

      const top = Number.parseFloat(styles.top);
      const elementBottom =
        (Number.isFinite(top) ? Math.max(top, 0) : 0) +
        element.getBoundingClientRect().height;

      return Math.max(currentBottom, elementBottom);
    },
    0,
  );

  document.body.style.setProperty(
    "--black-friday-2026-sticky-header-bottom",
    `${Math.ceil(stickyHeaderBottom)}px`,
  );
}

function initializeStickyAnchorOffset() {
  const stickyHeaderElements = getStickyHeaderElements();

  if (typeof ResizeObserver === "function") {
    stickyHeaderResizeObserver ||= new ResizeObserver(
      updateStickyAnchorOffset,
    );
    stickyHeaderResizeObserver.disconnect();
    stickyHeaderElements.forEach((element) =>
      stickyHeaderResizeObserver.observe(element),
    );
  }

  updateStickyAnchorOffset();
}

function getNormalFlowDocumentTop(element) {
  const inlinePosition = element.style.getPropertyValue("position");
  const inlinePriority = element.style.getPropertyPriority("position");

  element.style.setProperty("position", "static", "important");
  const documentTop = element.getBoundingClientRect().top + window.scrollY;

  if (inlinePosition) {
    element.style.setProperty("position", inlinePosition, inlinePriority);
  } else {
    element.style.removeProperty("position");
  }

  return documentTop;
}

function updateSection1MobileLayout(section) {
  const currentFrame = section1LayoutFrames.get(section);
  if (currentFrame) window.cancelAnimationFrame(currentFrame);

  const frame = window.requestAnimationFrame(() => {
    section1LayoutFrames.delete(section);
    const anchorTab = document.querySelector('[handle="Anchor-tab"]');
    const anchorSection = anchorTab?.closest(".shopify-section");

    if (!window.matchMedia("(max-width: 767px)").matches) {
      section.style.removeProperty(
        "--black-friday-2026-coupon-offset",
      );
      section.style.removeProperty(
        "--black-friday-2026-section1-richtext-top",
      );
      anchorSection?.style.removeProperty(
        "--black-friday-2026-section1-anchor-clearance",
      );
      return;
    }

    const couponContainer = getDirectChildren(
      section,
      '[handle="container"]',
    )[0];
    const richtext = getDirectChildren(
      section,
      ':is([handle="richtext"], [handle^="richtext-" i])',
    )[0];
    if (!couponContainer || !richtext) return;

    const coupons = getDirectChildren(
      couponContainer,
      '[handle^="image-" i]',
    );
    const couponImages = coupons
      .map((coupon) => coupon.querySelector("img"))
      .filter(Boolean);

    if (
      !coupons.length ||
      couponImages.some((image) => !image.complete)
    ) {
      return;
    }

    if (anchorSection) {
      anchorSection.classList.add("black-friday-2026-anchor-tab-section");
      anchorSection.style.setProperty(
        "--black-friday-2026-section1-anchor-clearance",
        "0px",
      );
    }

    const baseCouponOffset = 100;
    section.style.setProperty(
      "--black-friday-2026-coupon-offset",
      `${baseCouponOffset}px`,
    );

    const sectionRect = section.getBoundingClientRect();
    const firstRowCoupons = coupons.slice(0, 2);

    if (firstRowCoupons.length === 2) {
      const firstRowBottom = Math.max(
        ...firstRowCoupons.map(
          (coupon) => coupon.getBoundingClientRect().bottom,
        ),
      );
      const targetFirstRowBottom = sectionRect.bottom - 4;

      if (firstRowBottom > sectionRect.top) {
        section.style.setProperty(
          "--black-friday-2026-coupon-offset",
          `${Math.round(baseCouponOffset - (firstRowBottom - targetFirstRowBottom))}px`,
        );
      }
    }

    const couponBottom = Math.max(
      ...coupons.map((coupon) => coupon.getBoundingClientRect().bottom),
    );
    const richtextGap = 8;
    const anchorGap = 16;
    const richtextTop = couponBottom - sectionRect.top + richtextGap;

    section.style.setProperty(
      "--black-friday-2026-section1-richtext-top",
      `${Math.ceil(richtextTop)}px`,
    );

    if (anchorSection) {
      const contentBottom = Math.max(
        couponBottom,
        richtext.getBoundingClientRect().bottom,
      );
      const anchorTop = getNormalFlowDocumentTop(anchorSection);
      const contentBottomInDocument = contentBottom + window.scrollY;
      const anchorClearance = Math.max(
        0,
        contentBottomInDocument + anchorGap - anchorTop,
      );

      anchorSection.style.setProperty(
        "--black-friday-2026-section1-anchor-clearance",
        `${Math.ceil(anchorClearance)}px`,
      );
    }
  });

  section1LayoutFrames.set(section, frame);
}

function initializeSection1MobileLayout(root = document) {
  const sectionSelector = '[handle="section1"]';
  const closestSection = root.closest?.(sectionSelector);
  const sections = root.matches?.(sectionSelector)
    ? [root]
    : closestSection
      ? [closestSection]
      : root.querySelectorAll?.(sectionSelector) || [];

  sections.forEach((section) => {
    updateSection1MobileLayout(section);
    if (!("ResizeObserver" in window) || section1LayoutObservers.has(section)) {
      return;
    }

    const observer = new ResizeObserver(() =>
      updateSection1MobileLayout(section),
    );
    observer.observe(section);
    section
      .querySelectorAll(
        ':scope > *, :scope > [handle="container"] > [handle^="image-" i], :scope > [handle="container"] img',
      )
      .forEach((element) => observer.observe(element));
    section1LayoutObservers.set(section, observer);

    section.querySelectorAll(':scope > [handle="container"] img').forEach(
      (image) => {
        if (!image.complete) {
          image.addEventListener(
            "load",
            () => updateSection1MobileLayout(section),
            { once: true },
          );
          image.addEventListener(
            "error",
            () => updateSection1MobileLayout(section),
            { once: true },
          );
        }

        if (typeof image.decode === "function") {
          image
            .decode()
            .then(() => updateSection1MobileLayout(section))
            .catch(() => {});
        }
      },
    );
    document.fonts?.ready.then(() => updateSection1MobileLayout(section));
  });
}

function initializeAnchorTabs(root = document) {
  const anchorTabs = root.matches?.('[handle="Anchor-tab"]')
    ? [root]
    : root.querySelectorAll?.('[handle="Anchor-tab"]') || [];

  anchorTabs.forEach((anchorTab) => {
    anchorTab
      .closest(".shopify-section")
      ?.classList.add("black-friday-2026-anchor-tab-section");

    if (initializedAnchorTabs.has(anchorTab)) return;

    const anchors = getDirectChildren(anchorTab, '[handle="anchor"]');
    if (!anchors.length) return;

    const activateAnchor = (activeAnchor) => {
      anchors.forEach((anchor) => {
        const isActive = anchor === activeAnchor;
        const button = anchor.querySelector(':scope > [handle="button"]');

        anchor.classList.toggle("active", isActive);
        button?.setAttribute("aria-current", isActive ? "true" : "false");
      });
    };

    let mobileNavigationAnchor = null;
    let mobileNavigationSettleTimer = null;

    const keepMobileNavigationAnchorActive = () => {
      if (!mobileNavigationAnchor) return;

      const expectedAnchor = mobileNavigationAnchor;
      window.requestAnimationFrame(() => {
        if (mobileNavigationAnchor === expectedAnchor) {
          activateAnchor(expectedAnchor);
        }
      });

      window.clearTimeout(mobileNavigationSettleTimer);
      mobileNavigationSettleTimer = window.setTimeout(() => {
        activateAnchor(expectedAnchor);
        if (mobileNavigationAnchor === expectedAnchor) {
          mobileNavigationAnchor = null;
        }
      }, 180);
    };

    window.addEventListener("scroll", keepMobileNavigationAnchorActive, {
      passive: true,
    });

    anchors.forEach((anchor) => {
      anchor.addEventListener(
        "click",
        (event) => {
          activateAnchor(anchor);

          if (!window.matchMedia("(max-width: 767px)").matches) return;

          const anchorValue = (anchor.dataset.anchor || "")
            .trim()
            .replace(/^#/, "");
          const target = anchorValue
            ? document.getElementById(anchorValue) ||
              document.querySelector(
                `[handle="${CSS.escape(anchorValue)}"]`,
              )
            : null;

          if (!target) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          mobileNavigationAnchor = anchor;

          const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
          ).matches;
          const behavior = reduceMotion ? "auto" : "smooth";
          const anchorBounds = anchor.getBoundingClientRect();
          const tabBounds = anchorTab.getBoundingClientRect();
          const anchorLeft =
            anchorTab.scrollLeft +
            anchorBounds.left -
            tabBounds.left -
            (tabBounds.width - anchorBounds.width) / 2;

          anchorTab.scrollTo({
            left: Math.max(0, anchorLeft),
            behavior,
          });

          window.requestAnimationFrame(() => {
            updateStickyAnchorOffset();

            const stickyHeaderBottom = Number.parseFloat(
              window
                .getComputedStyle(document.body)
                .getPropertyValue(
                  "--black-friday-2026-sticky-header-bottom",
                ),
            );
            const targetTop =
              target.getBoundingClientRect().top +
              window.scrollY -
              (Number.isFinite(stickyHeaderBottom)
                ? stickyHeaderBottom
                : 0) -
              anchorTab.getBoundingClientRect().height;

            window.scrollTo({
              top: Math.max(0, Math.round(targetTop)),
              behavior,
            });
          });
        },
        true,
      );
    });

    initializedAnchorTabs.add(anchorTab);
    activateAnchor(
      anchors.find((anchor) => anchor.classList.contains("active")) || anchors[0],
    );

    if (window.matchMedia("(max-width: 767px)").matches) {
      window.requestAnimationFrame(() => {
        anchorTab.scrollLeft = 0;
      });
    }
  });
}

function getCouponCode(imageBlock) {
  const handle = imageBlock.getAttribute("handle") || "";
  return handle.match(/^image-(.+)$/i)?.[1]?.trim() || "";
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const helper = document.createElement("textarea");
  helper.value = value;
  helper.setAttribute("readonly", "");
  helper.style.cssText =
    "position: fixed; top: -9999px; left: -9999px; opacity: 0;";
  document.body.appendChild(helper);
  helper.focus();
  helper.select();

  const copied = document.execCommand("copy");
  helper.remove();
  if (!copied) throw new Error("Copy command failed");
}

function showCouponToast() {
  const message = "Code copied! Don't forget to use it at checkout.";

  if (typeof window.toast === "function" && document.querySelector(".toast-wrapper")) {
    window.toast(message);
    return;
  }

  let toast = document.getElementById("black-friday-2026-copy-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "black-friday-2026-copy-toast";
    toast.className = "black-friday-2026-copy-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.classList.add("is-visible");
  window.clearTimeout(couponToastTimer);
  couponToastTimer = window.setTimeout(() => {
    toast.classList.remove("is-visible");
  }, 1800);
}

async function copyCouponFromImage(imageBlock) {
  const couponCode = getCouponCode(imageBlock);
  if (!couponCode) return;

  try {
    await copyText(couponCode);
    showCouponToast();
  } catch (error) {
    console.error("Black Friday coupon copy error:", error);
  }
}

function initializeCouponImages(root = document) {
  const imageBlocks = root.matches?.(couponImageSelector)
    ? [root]
    : root.querySelectorAll?.(couponImageSelector) || [];

  imageBlocks.forEach((imageBlock) => {
    if (initializedCouponImages.has(imageBlock)) return;

    const image = imageBlock.querySelector("img");
    const couponCode = getCouponCode(imageBlock);
    if (!image || !couponCode) return;

    imageBlock.setAttribute("role", "button");
    imageBlock.setAttribute("tabindex", "0");
    imageBlock.setAttribute("aria-label", `Copy ${couponCode}`);

    image.addEventListener("click", () => copyCouponFromImage(imageBlock));
    imageBlock.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      copyCouponFromImage(imageBlock);
    });

    initializedCouponImages.add(imageBlock);
  });
}

function updateSection2ProductControls(productContainer) {
  const productTrack = productContainer.querySelector(
    ":scope > .black-friday-2026-section2-products-track",
  );
  const controls = productContainer.querySelector(
    ":scope > .black-friday-2026-section2-products-controls",
  );
  if (!productTrack || !controls) return;

  const previousButton = controls.querySelector('[data-direction="previous"]');
  const nextButton = controls.querySelector('[data-direction="next"]');
  const progress = controls.querySelector(
    ".black-friday-2026-section2-products-progress",
  );
  const progressValue = progress?.querySelector(
    ".black-friday-2026-section2-products-progress-value",
  );
  const maxScroll = Math.max(
    0,
    productTrack.scrollWidth - productTrack.clientWidth,
  );
  const filledProgress = productTrack.scrollWidth
    ? Math.min(
        1,
        (productTrack.scrollLeft + productTrack.clientWidth) /
          productTrack.scrollWidth,
      )
    : 1;

  controls.hidden = maxScroll <= 1;
  previousButton?.toggleAttribute("disabled", productTrack.scrollLeft <= 1);
  nextButton?.toggleAttribute(
    "disabled",
    productTrack.scrollLeft >= maxScroll - 1,
  );
  progress?.setAttribute(
    "aria-valuenow",
    String(Math.round(filledProgress * 100)),
  );

  if (progressValue) {
    progressValue.style.left = "0%";
    progressValue.style.width = `${filledProgress * 100}%`;
  }
}

function initializeSection2ProductControls(productContainer, productTrack) {
  let controls = getDirectChildren(
    productContainer,
    ".black-friday-2026-section2-products-controls",
  )[0];

  if (controls) {
    updateSection2ProductControls(productContainer);
    return;
  }

  controls = document.createElement("div");
  controls.className = "black-friday-2026-section2-products-controls";

  const buttonGroup = document.createElement("div");
  buttonGroup.className =
    "black-friday-2026-section2-products-navigation";

  const createNavigationButton = (direction, label, icon) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "black-friday-2026-section2-products-navigation-button";
    button.dataset.direction = direction;
    button.setAttribute("aria-label", label);
    button.textContent = icon;
    return button;
  };

  const previousButton = createNavigationButton(
    "previous",
    "Previous products",
    "\u2190",
  );
  const nextButton = createNavigationButton(
    "next",
    "Next products",
    "\u2192",
  );
  const progress = document.createElement("div");
  progress.className = "black-friday-2026-section2-products-progress";
  progress.setAttribute("role", "progressbar");
  progress.setAttribute("aria-label", "Product carousel progress");
  progress.setAttribute("aria-valuemin", "0");
  progress.setAttribute("aria-valuemax", "100");
  progress.setAttribute("aria-valuenow", "0");

  const progressValue = document.createElement("span");
  progressValue.className =
    "black-friday-2026-section2-products-progress-value";
  progress.appendChild(progressValue);
  buttonGroup.append(previousButton, nextButton);
  controls.append(buttonGroup, progress);
  productContainer.appendChild(controls);

  const scrollToAdjacentGroup = (direction) => {
    const slides = getDirectChildren(productTrack, "[data-embla-slide]");
    const trackLeft = productTrack.getBoundingClientRect().left;
    const groupPositions = slides
      .filter((slide, index) => index % 4 === 0)
      .map(
        (slide) =>
          slide.getBoundingClientRect().left -
          trackLeft +
          productTrack.scrollLeft,
      );
    const maxScroll = Math.max(
      0,
      productTrack.scrollWidth - productTrack.clientWidth,
    );
    const currentScroll = productTrack.scrollLeft;
    const target =
      direction > 0
        ? groupPositions.find((position) => position > currentScroll + 2) ??
          maxScroll
        : [...groupPositions]
            .reverse()
            .find((position) => position < currentScroll - 2) ?? 0;

    productTrack.scrollTo({
      left: Math.min(maxScroll, Math.max(0, target)),
      behavior: "smooth",
    });
  };

  previousButton.addEventListener("click", () => scrollToAdjacentGroup(-1));
  nextButton.addEventListener("click", () => scrollToAdjacentGroup(1));
  progress.addEventListener("click", (event) => {
    const bounds = progress.getBoundingClientRect();
    const maxScroll = Math.max(
      0,
      productTrack.scrollWidth - productTrack.clientWidth,
    );
    const clickProgress = bounds.width
      ? Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width))
      : 0;

    productTrack.scrollTo({
      left: clickProgress * maxScroll,
      behavior: "smooth",
    });
  });
  productTrack.addEventListener(
    "scroll",
    () => updateSection2ProductControls(productContainer),
    { passive: true },
  );

  window.requestAnimationFrame(() =>
    updateSection2ProductControls(productContainer),
  );
}

function updateSection2MobilePagination(productContainer) {
  const productTrack = productContainer.querySelector(
    ":scope > .black-friday-2026-section2-products-track",
  );
  const viewMoreButton = productContainer.querySelector(
    ":scope > .black-friday-2026-section2-view-more",
  );
  if (!productTrack || !viewMoreButton) return;

  const productSlides = getDirectChildren(productTrack, "[data-embla-slide]");
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  const visibleCount = Math.max(
    section2MobileBatchSize,
    Number.parseInt(productContainer.dataset.mobileVisibleCount, 10) ||
      section2MobileBatchSize,
  );

  productSlides.forEach((slide, index) => {
    slide.hidden = isMobile && index >= visibleCount;
  });

  viewMoreButton.hidden = !isMobile || visibleCount >= productSlides.length;
  viewMoreButton.setAttribute(
    "aria-expanded",
    String(!isMobile || visibleCount >= productSlides.length),
  );
}

function initializeSection2MobilePagination(productContainer) {
  let viewMoreButton = getDirectChildren(
    productContainer,
    ".black-friday-2026-section2-view-more",
  )[0];

  if (!viewMoreButton) {
    viewMoreButton = document.createElement("button");
    viewMoreButton.type = "button";
    viewMoreButton.className = "black-friday-2026-section2-view-more";
    viewMoreButton.textContent = "View More";
    viewMoreButton.addEventListener("click", () => {
      const currentVisibleCount =
        Number.parseInt(productContainer.dataset.mobileVisibleCount, 10) ||
        section2MobileBatchSize;
      productContainer.dataset.mobileVisibleCount = String(
        currentVisibleCount + section2MobileBatchSize,
      );
      updateSection2MobilePagination(productContainer);
      updateSection2MobileAlignment(productContainer);
    });
    productContainer.appendChild(viewMoreButton);
  }

  if (!productContainer.dataset.mobileVisibleCount) {
    productContainer.dataset.mobileVisibleCount = String(section2MobileBatchSize);
  }
  updateSection2MobilePagination(productContainer);
}

function updateSection2MobileAlignment(productContainer) {
  const currentFrame = section2AlignmentFrames.get(productContainer);
  if (currentFrame) window.cancelAnimationFrame(currentFrame);

  const frame = window.requestAnimationFrame(() => {
    section2AlignmentFrames.delete(productContainer);

    if (!window.matchMedia("(max-width: 767px)").matches) return;

    const productTrack = productContainer.querySelector(
      ":scope > .black-friday-2026-section2-products-track",
    );
    if (!productTrack) return;

    const slides = getDirectChildren(productTrack, "[data-embla-slide]").filter(
      (slide) => !slide.hidden,
    );
    const items = [];
    const slotNames = ["title", "rating", "price", "button"];
    const slotHeights = Object.fromEntries(
      slotNames.map((slotName) => [slotName, 0]),
    );

    slotNames.forEach((slotName) =>
      productContainer.style.removeProperty(
        `--black-friday-2026-section2-${slotName}-height`,
      ),
    );

    slides.forEach((slide) => {
      const card = slide.querySelector(':scope > [handle="product-card"]');
      const cardContent = card?.querySelector(
        ':scope > [handle="container"]',
      );
      const media = cardContent?.querySelector(
        ':scope > [handle="container"]:first-child',
      );
      const details = cardContent?.querySelector(
        ':scope > [handle="container"]:last-child',
      );
      if (!cardContent || !media || !details) return;

      const detailChildren = Array.from(details.children).filter(
        (child) => !child.matches("style, script"),
      );
      const getDirectSlot = (container, children, selector) => {
        const slotContent = container?.querySelector(selector);
        if (!slotContent) return null;

        return children.find(
          (child) => child === slotContent || child.contains(slotContent),
        );
      };
      const title = getDirectSlot(
        details,
        detailChildren,
        '[handle="title"]',
      );
      const rating = getDirectSlot(
        details,
        detailChildren,
        '[handle="product-rating"], .opinew-stars-plugin-product-list',
      );
      const purchase = getDirectSlot(
        details,
        detailChildren,
        '[handle="product-button"], [handle="product-sale-price"], [handle="product-compare-price"]',
      );
      const purchaseChildren = purchase
        ? Array.from(purchase.children).filter(
            (child) => !child.matches("style, script"),
          )
        : [];
      const price = getDirectSlot(
        purchase,
        purchaseChildren,
        '[handle="product-sale-price"], [handle="product-compare-price"]',
      );
      const button = getDirectSlot(
        purchase,
        purchaseChildren,
        '[handle="product-button"]',
      );

      media.style.removeProperty("--black-friday-2026-section2-media-height");
      details.style.removeProperty(
        "--black-friday-2026-section2-details-height",
      );

      details.classList.add("black-friday-2026-section2-mobile-details");
      title?.classList.add("black-friday-2026-section2-mobile-title");
      rating?.classList.add("black-friday-2026-section2-mobile-rating");
      purchase?.classList.add("black-friday-2026-section2-mobile-purchase");
      price?.classList.add("black-friday-2026-section2-mobile-price");
      button?.classList.add("black-friday-2026-section2-mobile-button");

      items.push({ media, details, title, rating, price, button });
    });

    if (!items.length) return;

    items.forEach((item) => {
      slotNames.forEach((slotName) => {
        const slot = item[slotName];
        if (!slot || window.getComputedStyle(slot).display === "none") return;

        const slotStyles = window.getComputedStyle(slot);
        const marginTop = Number.parseFloat(slotStyles.marginTop) || 0;
        const marginBottom = Number.parseFloat(slotStyles.marginBottom) || 0;
        slotHeights[slotName] = Math.max(
          slotHeights[slotName],
          slot.getBoundingClientRect().height + marginTop + marginBottom,
        );
      });
    });

    slotNames.forEach((slotName) => {
      productContainer.style.setProperty(
        `--black-friday-2026-section2-${slotName}-height`,
        `${Math.ceil(slotHeights[slotName])}px`,
      );
    });

    const mediaHeight = Math.max(
      ...items.map(({ media }) => media.getBoundingClientRect().height),
    );
    const detailsHeight = Math.max(
      ...items.map(({ details }) => details.getBoundingClientRect().height),
    );

    items.forEach(({ media, details }) => {
      media.style.setProperty(
        "--black-friday-2026-section2-media-height",
        `${Math.ceil(mediaHeight)}px`,
      );
      details.style.setProperty(
        "--black-friday-2026-section2-details-height",
        `${Math.ceil(detailsHeight)}px`,
      );
    });
  });

  section2AlignmentFrames.set(productContainer, frame);
}

function updateSection2DesktopAlignment(productContainer) {
  const currentFrame = section2DesktopAlignmentFrames.get(productContainer);
  if (currentFrame) window.cancelAnimationFrame(currentFrame);

  const frame = window.requestAnimationFrame(() => {
    section2DesktopAlignmentFrames.delete(productContainer);

    const productTrack = productContainer.querySelector(
      ":scope > .black-friday-2026-section2-products-track",
    );
    if (!productTrack) return;

    productTrack.style.removeProperty("grid-template-columns");
    getDirectChildren(productTrack, "[data-embla-slide]").forEach((slide) => {
      slide.style.removeProperty("grid-column");
      slide.style.removeProperty("grid-row");

      const productCard = slide.querySelector(
        ':scope > [handle="product-card"]',
      );
      const productContent = productCard?.querySelector(
        ':scope > [handle="container"]',
      );
      const detailContainer = productContent
        ? getDirectChildren(productContent, '[handle="container"]').find(
            (container) =>
              container.querySelector(':scope > [handle="title"]'),
          )
        : null;
      if (!detailContainer) return;

      const detailChildren = Array.from(detailContainer.children).filter(
        (child) => !child.matches("style, script"),
      );
      const getDetailSlot = (selector) => {
        const content = detailContainer.querySelector(selector);
        if (!content) return null;

        return detailChildren.find(
          (child) => child === content || child.contains(content),
        );
      };
      const title = getDetailSlot('[handle="title"]');
      const rating = getDetailSlot(
        '.opinew-stars-plugin-product-list, .loox-rating',
      );
      const price = getDetailSlot(
        '[handle="product-sale-price"], [handle="product-compare-price"]',
      );
      const button = getDetailSlot('[handle="product-button"]');
      const priceJustification = [
        ["md:justify-start", "flex-start"],
        ["md:justify-center", "center"],
        ["md:justify-end", "flex-end"],
        ["md:justify-between", "space-between"],
        ["md:justify-around", "space-around"],
      ].find(([className]) => price?.classList.contains(className))?.[1];

      detailContainer.classList.add(
        "black-friday-2026-section2-desktop-details",
      );
      title?.classList.add("black-friday-2026-section2-desktop-title");
      rating?.classList.add("black-friday-2026-section2-desktop-rating");
      price?.classList.add("black-friday-2026-section2-desktop-price");
      if (priceJustification) {
        price.style.setProperty(
          "--black-friday-2026-section2-price-justify",
          priceJustification,
        );
      } else {
        price?.style.removeProperty(
          "--black-friday-2026-section2-price-justify",
        );
      }
      button?.classList.add("black-friday-2026-section2-desktop-button");
    });
  });

  section2DesktopAlignmentFrames.set(productContainer, frame);
}

function initializeSection2ProductLayout(root = document) {
  const sections = root.matches?.('[handle="section2"]')
    ? [root]
    : root.querySelectorAll?.('[handle="section2"]') || [];

  sections.forEach((section) => {
    const productContainer = getDirectChildren(
      section,
      '[handle="container"]',
    ).find(
      (container) =>
        container.classList.contains(
          "black-friday-2026-section2-products",
        ) || getDirectChildren(container, "[data-embla-slide]").length > 0,
    );

    if (!productContainer) return;

    productContainer.classList.add(
      "black-friday-2026-section2-products",
    );

    let productTrack = getDirectChildren(
      productContainer,
      ".black-friday-2026-section2-products-track",
    )[0];
    const productSlides = getDirectChildren(
      productContainer,
      "[data-embla-slide]",
    );

    if (!productTrack && productSlides.length) {
      productTrack = document.createElement("div");
      productTrack.className =
        "black-friday-2026-section2-products-track";
      productContainer.insertBefore(productTrack, productSlides[0]);
      productSlides.forEach((slide) => productTrack.appendChild(slide));
    }

    if (productTrack) {
      productTrack.classList.toggle(
        "is-product-scrollable",
        getDirectChildren(productTrack, "[data-embla-slide]").length > 4,
      );
      initializeSection3ProductScroller(productTrack);
      initializeSection2MobilePagination(productContainer);
      updateSection2MobileAlignment(productContainer);
      updateSection2DesktopAlignment(productContainer);
      productTrack.querySelectorAll("img").forEach((image) => {
        if (image.complete || initializedSection2Images.has(image)) return;
        image.addEventListener(
          "load",
          () => updateSection2MobileAlignment(productContainer),
          { once: true },
        );
        initializedSection2Images.add(image);
      });
      document.fonts?.ready.then(() =>
        updateSection2MobileAlignment(productContainer),
      );
      initializeSection2ProductControls(productContainer, productTrack);
    }
  });
}

function initializeSection4ProductContainer(section, productContainer) {
    productContainer.classList.add(
      "black-friday-2026-section4-products-viewport",
    );

    let productTrack = productContainer.querySelector(
      ":scope > .black-friday-2026-section4-products-track",
    );
    const directSlides = getDirectChildren(
      productContainer,
      "[data-embla-slide]",
    ).filter((slide) =>
      slide.querySelector('[handle="product-card"]'),
    );

    if (!productTrack && directSlides.length) {
      productTrack = document.createElement("div");
      productTrack.className =
        "black-friday-2026-section4-products-track";
      productContainer.insertBefore(productTrack, directSlides[0]);
      directSlides.forEach((slide) => productTrack.appendChild(slide));
    }

    if (!productTrack) {
      const productSlides = Array.from(
        productContainer.querySelectorAll("[data-embla-slide]"),
      ).filter((slide) =>
        slide.querySelector('[handle="product-card"]'),
      );
      const candidateTrack = productSlides[0]?.parentElement;

      if (
        candidateTrack &&
        productSlides.every((slide) => slide.parentElement === candidateTrack)
      ) {
        productTrack = candidateTrack;
        productTrack.classList.add(
          "black-friday-2026-section4-products-track",
        );
      }
    }

    if (!productTrack) return;

    productTrack.classList.add(
      "black-friday-2026-mobile-product-scroller",
    );
    if (section.matches('[handle="section7"]')) {
      initializeSection7ProductScroller(productTrack);
    } else if (
      section.matches(
        ':is([handle="section4"], [handle="section5"], [handle="section6"])',
      )
    ) {
      initializeSection3ProductScroller(productTrack);
    }
    applyConfiguredMobileProductGap(productTrack, productContainer);

    productTrack.dataset.desktopProductCount = "4";

    const productSlides = getDirectChildren(
      productTrack,
      "[data-embla-slide]",
    );
    productTrack.classList.toggle(
      "is-product-scrollable",
      productSlides.length > Number(productTrack.dataset.desktopProductCount),
    );
    productSlides.forEach((slide) => {
      slide.classList.add("black-friday-2026-section4-product-slide");
      const productCard = slide.querySelector(
        ':scope > [handle="product-card"]',
      );
      if (productCard) {
        applyConfiguredMobileProductDimensions(slide, productCard);
      }
    });

    const configuredProductContainer = productTrack.querySelector(
      ':scope > [data-embla-slide] > [handle="product-card"] > [handle^="container"]',
    );
    const configuredWidth = getConfiguredStyleValue(
      [configuredProductContainer],
      "--w-pc",
    );

    if (configuredWidth) {
      productTrack.style.setProperty(
        "--black-friday-2026-section4-product-width",
        configuredWidth,
      );
    } else {
      productTrack.style.removeProperty(
        "--black-friday-2026-section4-product-width",
      );
    }

    initializeSection4ProductSpacing(productTrack);
    initializeSection4ProductAlignment(productTrack);
    initializeSection5ProductControls(section, productContainer, productTrack);
}

function initializeSection7ProductScroller(productTrack) {
  productTrack.classList.add(
    "black-friday-2026-section7-drag-scroller",
  );

  if (
    initializedSection7ProductScrollers.has(productTrack)
  ) {
    return;
  }

  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let gestureDirection = "";
  let didDrag = false;
  let suppressClickAfterDrag = false;
  let suppressClickTimer = null;

  const finishGesture = (event) => {
    if (pointerId !== event.pointerId) return;

    if (didDrag) {
      suppressClickAfterDrag = true;
      window.clearTimeout(suppressClickTimer);
      suppressClickTimer = window.setTimeout(() => {
        suppressClickAfterDrag = false;
      }, 80);
    }
    if (productTrack.hasPointerCapture?.(pointerId)) {
      productTrack.releasePointerCapture(pointerId);
    }

    pointerId = null;
    gestureDirection = "";
    didDrag = false;
    productTrack.classList.remove("is-dragging");
  };

  productTrack.addEventListener(
    "pointerdown",
    (event) => {
      if (
        !event.isPrimary ||
        event.pointerType !== "mouse" ||
        event.button !== 0 ||
        event.target.closest?.(".black-friday-2026-product-link")
      ) {
        return;
      }

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = productTrack.scrollLeft;
      gestureDirection = "";
      didDrag = false;
    },
    true,
  );
  productTrack.addEventListener(
    "pointermove",
    (event) => {
      if (pointerId !== event.pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      if (!gestureDirection) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
        if (Math.abs(deltaX) < Math.abs(deltaY) * 0.85) return;
        gestureDirection = "horizontal";
      }

      if (gestureDirection !== "horizontal") return;

      event.preventDefault();
      event.stopPropagation();
      if (!didDrag) {
        didDrag = true;
        productTrack.classList.add("is-dragging");
        productTrack.setPointerCapture?.(pointerId);
      }
      productTrack.scrollLeft = startScrollLeft - deltaX;
    },
    { passive: false, capture: true },
  );
  productTrack.addEventListener("pointerup", finishGesture, true);
  productTrack.addEventListener("pointercancel", finishGesture, true);
  productTrack.addEventListener("lostpointercapture", finishGesture);
  productTrack.addEventListener(
    "dragstart",
    (event) => {
      event.preventDefault();
    },
    true,
  );
  productTrack.addEventListener(
    "click",
    (event) => {
      if (event.target.closest?.(".black-friday-2026-product-link")) return;
      if (!suppressClickAfterDrag) return;
      suppressClickAfterDrag = false;
      window.clearTimeout(suppressClickTimer);
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true,
  );

  initializedSection7ProductScrollers.add(productTrack);
}

function initializeSection4ProductLayout(root = document) {
  const productSectionSelector =
    '[handle="section4"], [handle="section5"], [handle="section6"], [handle="section7"]';
  const sections = root.matches?.(productSectionSelector)
    ? [root]
    : root.querySelectorAll?.(productSectionSelector) || [];

  sections.forEach((section) => {
    const configuredProductContainers = Array.from(
      section.querySelectorAll(
        '[handle="container-product"], [handle^="container-product-"]',
      ),
    );
    const fallbackProductContainer = getDirectChildren(
      section,
      '[handle="container"]',
    ).find((container) =>
      container.querySelector('[data-embla-slide] [handle="product-card"]'),
    );
    const productContainers = configuredProductContainers.length
      ? configuredProductContainers
      : fallbackProductContainer
        ? [fallbackProductContainer]
        : [];

    productContainers.forEach((productContainer) =>
      initializeSection4ProductContainer(section, productContainer),
    );
  });
}

function updateSection5ProductControls(productContainer) {
  const productTrack = getDirectChildren(
    productContainer,
    ".black-friday-2026-section4-products-track",
  )[0];
  const controls = getDirectChildren(
    productContainer,
    ".black-friday-2026-section5-products-controls",
  )[0];

  if (!productTrack || !controls) return;

  const progress = controls.querySelector(
    ".black-friday-2026-section3-products-progress",
  );
  const progressValue = progress?.querySelector(
    ".black-friday-2026-section3-products-progress-value",
  );
  const maxScroll = Math.max(
    0,
    productTrack.scrollWidth - productTrack.clientWidth,
  );
  const filledProgress = productTrack.scrollWidth
    ? Math.min(
        1,
        (productTrack.scrollLeft + productTrack.clientWidth) /
          productTrack.scrollWidth,
      )
    : 1;
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  controls.hidden = !isMobile || maxScroll <= 1;
  progress?.setAttribute(
    "aria-valuenow",
    String(Math.round(filledProgress * 100)),
  );

  if (progressValue) {
    progressValue.style.left = "0%";
    progressValue.style.width = `${filledProgress * 100}%`;
  }
}

function initializeSection5ProductControls(
  section,
  productContainer,
  productTrack,
) {
  if (
    !section.matches(
      ':is([handle="section4"], [handle="section5"], [handle="section6"], [handle="section7"])',
    )
  ) {
    return;
  }

  let controls = getDirectChildren(
    productContainer,
    ".black-friday-2026-section5-products-controls",
  )[0];
  const footerContent = section.matches('[handle="section7"]')
    ? getDirectChildren(
        productContainer,
        '[handle="button-jion"], [handle="text"]',
      )[0]
    : null;

  if (!controls) {
    controls = document.createElement("div");
    controls.className = "black-friday-2026-section5-products-controls";

    const progress = document.createElement("div");
    progress.className = "black-friday-2026-section3-products-progress";
    progress.setAttribute("role", "progressbar");
    progress.setAttribute("aria-label", "Product carousel progress");
    progress.setAttribute("aria-valuemin", "0");
    progress.setAttribute("aria-valuemax", "100");
    progress.setAttribute("aria-valuenow", "0");

    const progressValue = document.createElement("span");
    progressValue.className =
      "black-friday-2026-section3-products-progress-value";
    progress.appendChild(progressValue);
    controls.appendChild(progress);
    productContainer.insertBefore(controls, footerContent || null);
  } else if (
    footerContent &&
    controls.nextElementSibling !== footerContent
  ) {
    productContainer.insertBefore(controls, footerContent);
  }

  if (!initializedSection5ProductControls.has(productTrack)) {
    const progress = controls.querySelector(
      ".black-friday-2026-section3-products-progress",
    );

    progress?.addEventListener("click", (event) => {
      const bounds = progress.getBoundingClientRect();
      const maxScroll = Math.max(
        0,
        productTrack.scrollWidth - productTrack.clientWidth,
      );
      const clickProgress = bounds.width
        ? Math.min(
            1,
            Math.max(0, (event.clientX - bounds.left) / bounds.width),
          )
        : 0;

      productTrack.scrollTo({
        left: clickProgress * maxScroll,
        behavior: "smooth",
      });
    });
    productTrack.addEventListener(
      "scroll",
      () => updateSection5ProductControls(productContainer),
      { passive: true },
    );
    initializedSection5ProductControls.add(productTrack);
  }

  updateSection5ProductControls(productContainer);
}

function updateSection4ProductSpacing(productTrack) {
  const productSlides = getDirectChildren(
    productTrack,
    ".black-friday-2026-section4-product-slide",
  );
  const firstProduct = productSlides[0];
  if (!firstProduct || productTrack.clientWidth <= 0) return;

  const productWidth = firstProduct.getBoundingClientRect().width;
  const desktopProductCount =
    Number(productTrack.dataset.desktopProductCount) || 4;
  const availableGap =
    (productTrack.clientWidth - productWidth * desktopProductCount) /
    Math.max(1, desktopProductCount - 1);

  productTrack.style.setProperty(
    "--black-friday-2026-section4-product-gap",
    `${Math.max(0, availableGap)}px`,
  );
}

function initializeSection4ProductSpacing(productTrack) {
  updateSection4ProductSpacing(productTrack);
  if (
    !("ResizeObserver" in window) ||
    section4SpacingObservers.has(productTrack)
  ) {
    return;
  }

  const observer = new ResizeObserver(() =>
    updateSection4ProductSpacing(productTrack),
  );
  observer.observe(productTrack);
  section4SpacingObservers.set(productTrack, observer);
}

const section4ProductSlotNames = [
  "media",
  "short-title",
  "title",
  "rating",
  "price",
  "coupon",
  "inventory",
  "button",
];

function getSection4ProductSlots(productContent) {
  const contentChildren = Array.from(productContent.children).filter(
    (child) => !child.matches("style, script"),
  );
  const media = contentChildren.find((child) =>
    child.matches('[handle="container"]'),
  );
  const title = contentChildren.find((child) =>
    child.matches('[handle="title"]'),
  );
  const rating = contentChildren.find((child) =>
    child.querySelector(":scope > .opinew-stars-plugin-product-list"),
  );
  const price = contentChildren.find(
    (child) => child !== media && child.matches('[handle="container"]'),
  );
  const configuredInventory = contentChildren.find((child) =>
    child.matches('[id^="product-inventory-"]'),
  );
  const coupon = productContent.closest('[handle="section4"]')
    ? contentChildren.find((child) => child.matches('[handle="image"]'))
    : null;
  const memberPriceImage = productContent.closest('[handle="section7"]')
    ? contentChildren.find((child) => child.matches('[handle="image"]'))
    : null;
  const inventory = configuredInventory || memberPriceImage;
  const button = contentChildren.find((child) =>
    child.matches('[handle="product-button"]'),
  );
  const assignedSlots = new Set([
    media,
    title,
    rating,
    price,
    coupon,
    inventory,
    button,
  ]);
  const titleIndex = title ? contentChildren.indexOf(title) : -1;
  const shortTitle = contentChildren.find(
    (child, index) =>
      index > contentChildren.indexOf(media) &&
      titleIndex > index &&
      !assignedSlots.has(child),
  );

  return {
    media,
    "short-title": shortTitle,
    title,
    rating,
    price,
    coupon,
    inventory,
    button,
  };
}

function updateSection4ProductAlignment(productTrack) {
  const rowHeights = Object.fromEntries(
    section4ProductSlotNames.map((slotName) => [slotName, 0]),
  );
  const productContents = productTrack.querySelectorAll(
    ':scope > [data-embla-slide] > [handle="product-card"] > [handle^="container"]',
  );
  if (!productContents.length) return;

  productContents.forEach((productContent) => {
    const productSlots = getSection4ProductSlots(productContent);
    productContent.classList.add(
      "black-friday-2026-section4-product-content",
    );

    section4ProductSlotNames.forEach((slotName) => {
      const productSlot = productSlots[slotName];
      if (!productSlot) return;

      productSlot.classList.add(
        "black-friday-2026-section4-product-slot",
        `black-friday-2026-section4-product-${slotName}`,
      );
      const slotStyles = window.getComputedStyle(productSlot);
      const marginTop = Number.parseFloat(slotStyles.marginTop) || 0;
      const marginBottom = Number.parseFloat(slotStyles.marginBottom) || 0;
      rowHeights[slotName] = Math.max(
        rowHeights[slotName],
        productSlot.getBoundingClientRect().height + marginTop + marginBottom,
      );
    });
  });

  const gapProperty = window.matchMedia("(max-width: 767px)").matches
    ? "--gap-mb"
    : "--gap-pc";
  const configuredGap = Number.parseFloat(
    window
      .getComputedStyle(productContents[0])
      .getPropertyValue(gapProperty),
  );
  section4ProductSlotNames.forEach((slotName, slotIndex) => {
    const hasLaterContent = section4ProductSlotNames
      .slice(slotIndex + 1)
      .some((laterSlotName) => rowHeights[laterSlotName] > 0);
    const isAdjacentToRating =
      rowHeights.rating > 0 &&
      (slotName === "title" || slotName === "rating");
    const rowGap =
      rowHeights[slotName] > 0 &&
      hasLaterContent &&
      !isAdjacentToRating &&
      Number.isFinite(configuredGap)
        ? configuredGap
        : 0;

    productTrack.style.setProperty(
      `--black-friday-2026-section4-${slotName}-height`,
      `${Math.ceil(rowHeights[slotName] + rowGap)}px`,
    );
  });
}

function scheduleSection4ProductAlignment(productTrack) {
  const currentFrame = section4AlignmentFrames.get(productTrack);
  if (currentFrame) window.cancelAnimationFrame(currentFrame);

  const frame = window.requestAnimationFrame(() => {
    section4AlignmentFrames.delete(productTrack);
    updateSection4ProductAlignment(productTrack);
  });
  section4AlignmentFrames.set(productTrack, frame);
}

function initializeSection4ProductAlignment(productTrack) {
  updateSection4ProductAlignment(productTrack);
  if (section4AlignmentObservers.has(productTrack)) return;

  const observer = new MutationObserver(() =>
    scheduleSection4ProductAlignment(productTrack),
  );
  observer.observe(productTrack, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  section4AlignmentObservers.set(productTrack, observer);
}

const section3ProductSlotNames = ["title", "rating", "price"];

function getSection3ProductSlots(productCard) {
  const productContent = getProductContentContainer(productCard);
  const detailContainer = productContent
    ? getDirectChildren(productContent, '[handle="container"]').find(
        (container) =>
          getDirectChildren(container, '[handle="title"]').length > 0,
      )
    : null;

  if (!detailContainer) return null;

  const detailChildren = Array.from(detailContainer.children).filter(
    (child) => !child.matches("style, script"),
  );
  const getSlotContainer = (selector) => {
    const slotContent = detailContainer.querySelector(selector);
    if (!slotContent) return null;

    return detailChildren.find(
      (child) => child === slotContent || child.contains(slotContent),
    );
  };
  const title = getSlotContainer('[handle="title"]');
  const rating = getSlotContainer(
    '[handle="product-rating"], .opinew-stars-plugin-product-list',
  );
  const price = getSlotContainer(
    '[handle="product-sale-price"], [handle="product-compare-price"]',
  );

  return { detailContainer, title, rating, price };
}

function updateSection3ProductAlignment(productTrack) {
  const rowHeights = Object.fromEntries(
    section3ProductSlotNames.map((slotName) => [slotName, 0]),
  );
  const productCards = Array.from(
    productTrack.querySelectorAll(
      ':scope > [data-embla-slide] > [handle="product-card"]',
    ),
  );
  if (!productCards.length || productTrack.hidden) return;

  productCards.forEach((productCard) => {
    const productSlots = getSection3ProductSlots(productCard);
    if (!productSlots) return;

    productSlots.detailContainer.classList.add(
      "black-friday-2026-section3-product-details",
    );

    section3ProductSlotNames.forEach((slotName) => {
      const productSlot = productSlots[slotName];
      if (!productSlot) return;

      productSlot.classList.add(
        "black-friday-2026-section3-product-slot",
        `black-friday-2026-section3-product-${slotName}`,
      );
      const slotStyles = window.getComputedStyle(productSlot);
      const marginTop = Number.parseFloat(slotStyles.marginTop) || 0;
      const marginBottom = Number.parseFloat(slotStyles.marginBottom) || 0;
      rowHeights[slotName] = Math.max(
        rowHeights[slotName],
        productSlot.getBoundingClientRect().height + marginTop + marginBottom,
      );
    });
  });

  section3ProductSlotNames.forEach((slotName) => {
    productTrack.style.setProperty(
      `--black-friday-2026-section3-${slotName}-height`,
      `${Math.ceil(rowHeights[slotName])}px`,
    );
  });
}

function scheduleSection3ProductAlignment(productTrack) {
  const currentFrame = section3AlignmentFrames.get(productTrack);
  if (currentFrame) window.cancelAnimationFrame(currentFrame);

  const frame = window.requestAnimationFrame(() => {
    section3AlignmentFrames.delete(productTrack);
    updateSection3ProductAlignment(productTrack);
  });
  section3AlignmentFrames.set(productTrack, frame);
}

function initializeSection3ProductAlignment(productTrack) {
  updateSection3ProductAlignment(productTrack);
  if (section3AlignmentObservers.has(productTrack)) return;

  const observer = new MutationObserver(() =>
    scheduleSection3ProductAlignment(productTrack),
  );
  observer.observe(productTrack, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  section3AlignmentObservers.set(productTrack, observer);
}

function initializeMobileProductScrollers(root = document) {
  const slides = root.matches?.("[data-embla-slide]")
    ? [root]
    : root.querySelectorAll?.("[data-embla-slide]") || [];
  const productContainers = new Set(
    Array.from(slides, (slide) => slide.parentElement).filter(Boolean),
  );

  productContainers.forEach((container) => {
    const productSlides = getDirectChildren(
      container,
      "[data-embla-slide]",
    );
    const containsProductCards = productSlides.some((slide) =>
      slide.querySelector(':scope > [handle="product-card"]'),
    );

    if (containsProductCards) {
      container.classList.add(
        "black-friday-2026-mobile-product-scroller",
      );

      productSlides.forEach((slide) => {
        const productCard = slide.querySelector(
          ':scope > [handle="product-card"]',
        );
        if (!productCard) return;
        applyConfiguredMobileProductDimensions(slide, productCard);
      });
    }
  });
}

function updateSection3TabOrientation(root = document) {
  const tabLists = root.matches?.(
    '[handle="section3"] [handle="container-tab"]',
  )
    ? [root]
    : root.querySelectorAll?.(
        '[handle="section3"] [handle="container-tab"]',
      ) || [];
  const orientation = window.matchMedia("(max-width: 767px)").matches
    ? "horizontal"
    : "vertical";

  tabLists.forEach((tabList) =>
    tabList.setAttribute("aria-orientation", orientation),
  );
}

function updateSection3ProductControls(productList) {
  const controls = productList.querySelector(
    ":scope > .black-friday-2026-section3-products-controls",
  );
  const activeProduct = getDirectChildren(
    productList,
    '[handle="product"].is-active:not([hidden])',
  )[0];

  if (!controls || !activeProduct) return;

  const progress = controls.querySelector(
    ".black-friday-2026-section3-products-progress",
  );
  const progressValue = progress?.querySelector(
    ".black-friday-2026-section3-products-progress-value",
  );
  const maxScroll = Math.max(
    0,
    activeProduct.scrollWidth - activeProduct.clientWidth,
  );
  const filledProgress = activeProduct.scrollWidth
    ? Math.min(
        1,
        (activeProduct.scrollLeft + activeProduct.clientWidth) /
          activeProduct.scrollWidth,
      )
    : 1;

  controls.hidden = maxScroll <= 1;
  progress?.setAttribute(
    "aria-valuenow",
    String(Math.round(filledProgress * 100)),
  );

  if (progressValue) {
    progressValue.style.left = "0%";
    progressValue.style.width = `${filledProgress * 100}%`;
  }
}

function initializeSection3ProductControls(productList, products) {
  let controls = getDirectChildren(
    productList,
    ".black-friday-2026-section3-products-controls",
  )[0];

  if (controls) {
    updateSection3ProductControls(productList);
    return;
  }

  controls = document.createElement("div");
  controls.className = "black-friday-2026-section3-products-controls";
  const progress = document.createElement("div");
  progress.className = "black-friday-2026-section3-products-progress";
  progress.setAttribute("role", "progressbar");
  progress.setAttribute("aria-label", "Product carousel progress");
  progress.setAttribute("aria-valuemin", "0");
  progress.setAttribute("aria-valuemax", "100");
  progress.setAttribute("aria-valuenow", "0");

  const progressValue = document.createElement("span");
  progressValue.className =
    "black-friday-2026-section3-products-progress-value";
  progress.appendChild(progressValue);
  controls.appendChild(progress);
  productList.appendChild(controls);

  const getActiveProduct = () =>
    getDirectChildren(
      productList,
      '[handle="product"].is-active:not([hidden])',
    )[0];

  progress.addEventListener("click", (event) => {
    const activeProduct = getActiveProduct();
    if (!activeProduct) return;

    const bounds = progress.getBoundingClientRect();
    const maxScroll = Math.max(
      0,
      activeProduct.scrollWidth - activeProduct.clientWidth,
    );
    const clickProgress = bounds.width
      ? Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width))
      : 0;

    activeProduct.scrollTo({
      left: clickProgress * maxScroll,
      behavior: "smooth",
    });
  });

  products.forEach((product) => {
    product.addEventListener(
      "scroll",
      () => {
        if (product.classList.contains("is-active")) {
          updateSection3ProductControls(productList);
        }
      },
      { passive: true },
    );
  });
}

function initializeSection3Tabs(root = document) {
  const sections = root.matches?.('[handle="section3"]')
    ? [root]
    : root.querySelectorAll?.('[handle="section3"]') || [];

  sections.forEach((section) => {
    const tabList = section.querySelector('[handle="container-tab"]');
    const productList = section.querySelector(
      ':scope > [handle="container-product"]',
    );

    if (!tabList || !productList || initializedTabLists.has(tabList)) return;

    const tabs = getDirectChildren(tabList, '[handle="text"]');
    const products = getDirectChildren(productList, '[handle="product"]');
    const itemCount = Math.min(tabs.length, products.length);

    if (!itemCount) return;

    products.forEach((product) => {
      initializeProductGrid(product);
      initializeSection3ProductScroller(product);
      initializeSection3ProductAlignment(product);
    });
    initializeSection3ProductControls(productList, products);

    const sectionId = section.id || "black-friday-2026-section3";
    const activateTab = (nextIndex, moveFocus = false) => {
      const activeIndex = (nextIndex + itemCount) % itemCount;

      tabs.forEach((tab, index) => {
        const isActive = index === activeIndex && index < itemCount;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      products.forEach((product, index) => {
        const isActive = index === activeIndex && index < itemCount;
        product.classList.toggle("is-active", isActive);
        product.hidden = !isActive;
      });

      if (window.matchMedia("(max-width: 767px)").matches) {
        const activeTab = tabs[activeIndex];
        const targetLeft =
          activeTab.offsetLeft -
          (tabList.clientWidth - activeTab.offsetWidth) / 2;

        tabList.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: moveFocus ? "smooth" : "auto",
        });
      }

      window.requestAnimationFrame(() => {
        updateSection3ProductControls(productList);
        scheduleSection3ProductAlignment(products[activeIndex]);
      });

      if (moveFocus) tabs[activeIndex].focus();
    };

    tabList.setAttribute("role", "tablist");
    updateSection3TabOrientation(section);

    tabs.forEach((tab, index) => {
      const panel = products[index];
      if (!panel) {
        tab.hidden = true;
        return;
      }

      const tabId = tab.id || `${sectionId}-tab-${index + 1}`;
      const panelId = `${sectionId}-panel-${index + 1}`;
      tab.id = tabId;
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-controls", panelId);
      panel.id = panelId;
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", tabId);

      tab.addEventListener("click", (event) => {
        event.preventDefault();
        activateTab(index);
      });

      tab.addEventListener("keydown", (event) => {
        let nextIndex = null;

        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
          nextIndex = index + 1;
        } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
          nextIndex = index - 1;
        } else if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = itemCount - 1;
        } else if (event.key === "Enter" || event.key === " ") {
          nextIndex = index;
        }

        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(nextIndex, true);
      });
    });

    initializedTabLists.add(tabList);
    activateTab(0);
  });
}

function initializeSection7Tabs(root = document) {
  const sections = root.matches?.('[handle="section7"]')
    ? [root]
    : root.querySelectorAll?.('[handle="section7"]') || [];

  sections.forEach((section) => {
    const tabList = section.querySelector(':scope > [handle="container-tab"]');
    const contentContainer = getDirectChildren(
      section,
      '[handle="container"]',
    ).find((container) =>
      getDirectChildren(container, '[handle^="container-"]').length,
    );

    if (
      !tabList ||
      !contentContainer ||
      initializedSection7TabLists.has(tabList)
    ) {
      return;
    }

    const tabs = getDirectChildren(tabList, '[handle^="text-"]');
    const panels = getDirectChildren(
      contentContainer,
      '[handle^="container-"]',
    );
    const getPanelBaseHandle = (panel) =>
      (panel.getAttribute("handle") || "").replace(/-(?:in|out)$/, "");
    const getPanelCustomerState = (panel) => {
      const handle = panel.getAttribute("handle") || "";

      if (handle.endsWith("-in")) return "logged-in";
      if (handle.endsWith("-out")) return "logged-out";
      return "all";
    };
    const isCustomerLoggedIn = () => window.customerInfo != null;
    const tabPanels = tabs
      .map((tab) => {
        const tabHandle = tab.getAttribute("handle") || "";
        const panelHandle = `container-${tabHandle.replace(/^text-/, "")}`;
        const relatedPanels = panels.filter(
          (candidate) => getPanelBaseHandle(candidate) === panelHandle,
        );
        return relatedPanels.length ? { tab, panels: relatedPanels } : null;
      })
      .filter(Boolean);

    if (!tabPanels.length) return;

    const sectionId = section.id || "black-friday-2026-section7";
    const mobileTabMedia = window.matchMedia("(max-width: 767px)");
    let hasActivatedTab = false;
    let stableMobileContentHeight = 0;
    let mobilePositionSnapshot = null;
    let mobileActivationId = 0;
    const setMobileContentMinHeight = (height) => {
      if (!mobileTabMedia.matches || !Number.isFinite(height)) return;

      stableMobileContentHeight = Math.max(
        stableMobileContentHeight,
        Math.ceil(height),
      );
      contentContainer.style.setProperty(
        "--black-friday-2026-section7-mobile-content-height",
        `${stableMobileContentHeight}px`,
      );
    };
    const rememberMobileContentHeight = () => {
      if (!mobileTabMedia.matches) return;

      const contentHeight = Math.ceil(
        Math.max(
          contentContainer.getBoundingClientRect().height,
          contentContainer.scrollHeight,
        ),
      );
      setMobileContentMinHeight(contentHeight);
    };
    const measurePanelHeight = (panel) => {
      if (!mobileTabMedia.matches || !panel) return 0;
      if (!panel.hidden) {
        return panel.getBoundingClientRect().height;
      }

      const previousStyles = {
        display: panel.style.display,
        position: panel.style.position,
        visibility: panel.style.visibility,
        pointerEvents: panel.style.pointerEvents,
        width: panel.style.width,
      };
      const wasHidden = panel.hidden;
      panel.hidden = false;
      panel.style.position = "absolute";
      panel.style.visibility = "hidden";
      panel.style.pointerEvents = "none";
      panel.style.width = "100%";
      const panelStyles = window.getComputedStyle(panel);
      const marginTop = Number.parseFloat(panelStyles.marginTop) || 0;
      const marginBottom = Number.parseFloat(panelStyles.marginBottom) || 0;
      const height =
        panel.getBoundingClientRect().height + marginTop + marginBottom;
      panel.hidden = wasHidden;
      panel.style.display = previousStyles.display;
      panel.style.position = previousStyles.position;
      panel.style.visibility = previousStyles.visibility;
      panel.style.pointerEvents = previousStyles.pointerEvents;
      panel.style.width = previousStyles.width;
      return height;
    };
    const getVisiblePanel = ({ panels: relatedPanels }) => {
      const customerState = isCustomerLoggedIn()
        ? "logged-in"
        : "logged-out";

      return (
        relatedPanels.find(
          (panel) => getPanelCustomerState(panel) === customerState,
        ) ||
        relatedPanels.find(
          (panel) => getPanelCustomerState(panel) === "all",
        )
      );
    };
    const activateTab = (nextIndex, moveFocus = false) => {
      const activationId = ++mobileActivationId;
      const activeIndex =
        (nextIndex + tabPanels.length) % tabPanels.length;
      const activePanel = getVisiblePanel(tabPanels[activeIndex]);
      const loggedIn = isCustomerLoggedIn();
      const preserveMobileTabPosition =
        mobileTabMedia.matches && hasActivatedTab;
      mobilePositionSnapshot = preserveMobileTabPosition
        ? {
            pageScrollLeft: window.scrollX,
            pageScrollTop: window.scrollY,
            tabViewportTop: tabList.getBoundingClientRect().top,
          }
        : null;

      if (preserveMobileTabPosition) {
        rememberMobileContentHeight();
        setMobileContentMinHeight(
          Math.max(
            contentContainer.getBoundingClientRect().height,
            measurePanelHeight(activePanel),
          ),
        );
      }

      const restoreMobilePagePosition = () => {
        if (!mobilePositionSnapshot) return;

        const { pageScrollLeft, pageScrollTop, tabViewportTop } =
          mobilePositionSnapshot;
        window.scrollTo({
          left: pageScrollLeft,
          top: pageScrollTop,
          behavior: "auto",
        });

        const tabOffset =
          tabList.getBoundingClientRect().top - tabViewportTop;
        if (Math.abs(tabOffset) > 0.5) window.scrollBy(0, tabOffset);
      };

      section.classList.toggle("is-customer-logged-in", loggedIn);
      section.classList.toggle("is-customer-logged-out", !loggedIn);
      section.dataset.customerState = loggedIn ? "logged-in" : "logged-out";

      tabPanels.forEach((tabPanel, index) => {
        const { tab } = tabPanel;
        const isActive = index === activeIndex;
        const controlledPanel = getVisiblePanel(tabPanel);

        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        if (controlledPanel) {
          tab.setAttribute("aria-controls", controlledPanel.id);
        }
      });

      panels.forEach((panel) => {
        const isActive = panel === activePanel;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
        panel.inert = !isActive;
        panel.setAttribute("aria-hidden", String(!isActive));
      });
      restoreMobilePagePosition();
      hasActivatedTab = true;

      window.requestAnimationFrame(() => {
        rememberMobileContentHeight();
        restoreMobilePagePosition();

        const productContainer = activePanel?.matches(
          '[handle^="container-product"]',
        )
          ? activePanel
          : activePanel?.querySelector(
              '[handle^="container-product"]',
            );

        if (productContainer) {
          initializeSection4ProductContainer(section, productContainer);
        }

        const productTrack = productContainer?.querySelector(
          ".black-friday-2026-section4-products-track",
        );

        if (productContainer && productTrack) {
          productTrack.classList.add(
            "black-friday-2026-mobile-product-scroller",
          );
          initializeSection7ProductScroller(productTrack);
          getDirectChildren(productTrack, "[data-embla-slide]").forEach(
            (slide) => {
              const productCard = slide.querySelector(
                ':scope > [handle="product-card"]',
              );

              if (!productCard) return;
              slide.classList.add(
                "black-friday-2026-section4-product-slide",
              );
              applyConfiguredMobileProductDimensions(slide, productCard);
            },
          );
          updateSection5ProductControls(productContainer);
          updateSection4ProductSpacing(productTrack);
          scheduleSection4ProductAlignment(productTrack);
        }
        window.requestAnimationFrame(() => {
          rememberMobileContentHeight();
          restoreMobilePagePosition();
          if (activationId === mobileActivationId) {
            mobilePositionSnapshot = null;
          }
        });
      });

      if (moveFocus) {
        tabPanels[activeIndex].tab.focus({ preventScroll: true });
      }
    };

    tabList.setAttribute("role", "tablist");
    tabList.addEventListener(
      "click",
      (event) => {
        const activeIndex = tabPanels.findIndex(
          ({ tab }) => tab === event.target || tab.contains(event.target),
        );
        if (activeIndex < 0) return;

        event.preventDefault();
        event.stopPropagation();
        activateTab(activeIndex);
        if (event.detail > 0) tabPanels[activeIndex].tab.blur();
      },
      true,
    );
    tabPanels.forEach(({ tab, panels: relatedPanels }, index) => {
      const tabId = tab.id || `${sectionId}-tab-${index + 1}`;

      tab.id = tabId;
      tab.setAttribute("role", "tab");
      relatedPanels.forEach((panel, panelIndex) => {
        panel.id =
          panel.id || `${sectionId}-panel-${index + 1}-${panelIndex + 1}`;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tabId);
      });

      tab.addEventListener("shopify:block:select", () => {
        activateTab(index);
      });

      tab.addEventListener("keydown", (event) => {
        let nextIndex = null;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextIndex = index + 1;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextIndex = index - 1;
        } else if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = tabPanels.length - 1;
        } else if (event.key === "Enter" || event.key === " ") {
          nextIndex = index;
        }

        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(nextIndex, true);
      });
    });

    initializedSection7TabLists.add(tabList);
    activateTab(0);
    if ("ResizeObserver" in window) {
      const contentHeightObserver = new ResizeObserver(() =>
        rememberMobileContentHeight(),
      );
      contentHeightObserver.observe(contentContainer);
    }
  });
}

function initializeSection7JoinButtons(root = document) {
  const sectionSelector = '[handle="section7"]';
  const closestSection = root.closest?.(sectionSelector);
  const sections = root.matches?.(sectionSelector)
    ? [root]
    : closestSection
      ? [closestSection]
      : root.querySelectorAll?.(sectionSelector) || [];

  sections.forEach((section) => {
    section
      .querySelectorAll(
        '[handle="button-jion"], [handle="container-product-out"] > [handle="text"]',
      )
      .forEach((joinTrigger) => {
        if (initializedSection7JoinButtons.has(joinTrigger)) return;

        const isTextTrigger = joinTrigger.matches('[handle="text"]');
        if (isTextTrigger) {
          joinTrigger.classList.add(
            "black-friday-2026-section7-register-trigger",
          );
          joinTrigger.setAttribute("role", "button");
          joinTrigger.setAttribute("tabindex", "0");
        } else {
          joinTrigger.type = "button";
        }

        joinTrigger.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (window.customerInfo != null) return;

          joinTrigger.setAttribute("aria-busy", "true");
          getSection7AuthModule()
            .then(({ useRegisterStore }) => {
              if (!useRegisterStore?.getState) {
                throw new Error("Membership register dialog is unavailable.");
              }
              useRegisterStore.getState().openRegister();
            })
            .catch((error) => {
              console.error("Black Friday membership dialog error:", error);
            })
            .finally(() => joinTrigger.removeAttribute("aria-busy"));
        });

        if (isTextTrigger) {
          joinTrigger.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;

            event.preventDefault();
            joinTrigger.click();
          });
        }

        initializedSection7JoinButtons.add(joinTrigger);
      });
  });
}

function trackSection7Event(eventName) {
  if (
    !eventName ||
    !window.ptengine ||
    typeof window.ptengine.track !== "function"
  ) {
    return;
  }

  window.ptengine.track(
    eventName,
    { section: "black-friday-2026" },
    { transport: "beacon" },
  );
}

function initializeSection7Tracking(root = document) {
  const sectionSelector = '[handle="section7"]';
  const closestSection = root.closest?.(sectionSelector);
  const sections = root.matches?.(sectionSelector)
    ? [root]
    : closestSection
      ? [closestSection]
      : root.querySelectorAll?.(sectionSelector) || [];
  const tabEvents = {
    "text-free": "black-friday-2026-order-click",
    "text-product": "black-friday-2026-member-click",
    "text-get25": "black-friday-2026-referrals-click",
  };

  sections.forEach((section) => {
    if (initializedSection7Tracking.has(section)) return;

    section.addEventListener(
      "click",
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : event.target?.parentElement;
        if (!target) return;

        const tab = target.closest(
          '[handle="container-tab"] > [handle^="text-"]',
        );
        if (tab && section.contains(tab)) {
          trackSection7Event(tabEvents[tab.getAttribute("handle")]);
          return;
        }

        if (window.customerInfo == null) {
          const orderJoin = target.closest(
            '[handle="container-free-out"] [handle="button-jion"]',
          );
          if (orderJoin && section.contains(orderJoin)) {
            trackSection7Event("black-friday-2026-order-join");
            return;
          }

          const memberJoin = target.closest(
            '[handle="container-product-out"] > [handle="text"]',
          );
          if (memberJoin && section.contains(memberJoin)) {
            trackSection7Event("black-friday-2026-member-join");
          }
          return;
        }

        const productButton = target.closest('[handle="product-button"]');
        if (!productButton || !section.contains(productButton)) return;

        if (productButton.closest('[handle="container-free-in"]')) {
          trackSection7Event("black-friday-2026-order-add");
        } else if (
          productButton.closest('[handle="container-product-in"]')
        ) {
          trackSection7Event("black-friday-2026-member-add");
        }
      },
      true,
    );

    initializedSection7Tracking.add(section);
  });
}

function initializeSection7Referrals(root = document) {
  const containerSelector =
    '[handle="section7"] [handle="container-get25"]';
  const closestContainer = root.closest?.(containerSelector);
  const containers = root.matches?.(containerSelector)
    ? [root]
    : closestContainer
      ? [closestContainer]
      : root.querySelectorAll?.(containerSelector) || [];

  containers.forEach((container) => {
    if (initializedSection7ReferralContainers.has(container)) return;

    let formMount = getDirectChildren(
      container,
      ".black-friday-2026-referrals-form-mount",
    )[0];

    if (!formMount) {
      formMount = document.createElement("div");
      formMount.className = "black-friday-2026-referrals-form-mount";
      container.append(formMount);
    }

    initializedSection7ReferralContainers.add(container);
    container.classList.add("black-friday-2026-referrals-form");
    container.setAttribute("aria-busy", "true");

    getSection7ReferralsDependencies()
      .then(([{ clientExports, jsxRuntimeExports }, { LoginLayout }]) => {
        if (!clientExports?.createRoot || !jsxRuntimeExports?.jsx || !LoginLayout) {
          throw new Error("Black Friday referrals form is unavailable.");
        }

        clientExports
          .createRoot(formMount)
          .render(jsxRuntimeExports.jsx(LoginLayout, {}));
      })
      .catch((error) => {
        initializedSection7ReferralContainers.delete(container);
        container.classList.remove("black-friday-2026-referrals-form");
        console.error("Black Friday referrals form error:", error);
      })
      .finally(() => container.removeAttribute("aria-busy"));
  });
}

function initializePage(root = document) {
  const page = document.querySelector(pageSelector);
  if (!page) return;

  initializeSection7MemberPrices(root);
  observeSection7MemberPrices(page);
  initializeStickyAnchorOffset();
  initializeSection1MobileLayout(root);
  initializeAnchorTabs(root);
  initializeCouponImages(root);
  initializeSection2ProductLayout(root);
  initializeSection4ProductLayout(root);
  initializeSection3Tabs(root);
  initializeProductLinks(root);
  updateSection2And4MobileCartButtons(root);
  updateSection3MobileCards(root);
  updateSection5MobileCards(root);
  updateSection6MobileCartButtons(root);
  updateSection6CountdownLayout(root);
  initializeSection7Tracking(root);
  initializeSection7Tabs(root);
  initializeSection7JoinButtons(root);
  initializeSection7Referrals(root);
  initializeMobileProductScrollers(root);
  initializeDiscountBadges(root);
  initializeMobileQuickbuyButtons(root);
  initializeProductDiscountCodes(root);
  initializeBackToTop();
  page.classList.add(readyClass);
  page.dispatchEvent(
    new CustomEvent(initializeEventName, {
      detail: { root },
    }),
  );
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializePage(), {
    once: true,
  });
} else {
  initializePage();
}

document.addEventListener("shopify:section:load", (event) => {
  initializePage(event.target);
});

window.addEventListener("pageshow", () => {
  const updateLayouts = () => {
    document
      .querySelectorAll('[handle="section1"]')
      .forEach(updateSection1MobileLayout);
  };

  updateLayouts();
  window.requestAnimationFrame(() =>
    window.requestAnimationFrame(updateLayouts),
  );
});

window.addEventListener("resize", updateStickyAnchorOffset, { passive: true });
window.addEventListener(
  "resize",
  () => {
    document
      .querySelectorAll('[handle="section1"]')
      .forEach(updateSection1MobileLayout);
    document
      .querySelectorAll(".black-friday-2026-section2-products")
      .forEach((productContainer) => {
        updateSection2MobilePagination(productContainer);
        updateSection2MobileAlignment(productContainer);
        updateSection2DesktopAlignment(productContainer);
        updateSection2ProductControls(productContainer);
      });
    document
      .querySelectorAll('[handle="section3"] > [handle="container-product"]')
      .forEach(updateSection3ProductControls);
    document
      .querySelectorAll(
        '[handle="section3"] > [handle="container-product"] > [handle="product"].is-active:not([hidden])',
      )
      .forEach(scheduleSection3ProductAlignment);
    updateSection2And4MobileCartButtons();
    updateSection3MobileCards();
    updateSection5MobileCards();
    updateSection6MobileCartButtons();
    updateSection6CountdownLayout();
    document
      .querySelectorAll(".black-friday-2026-section4-products-track")
      .forEach((productTrack) => {
        updateSection4ProductSpacing(productTrack);
        scheduleSection4ProductAlignment(productTrack);
      });
    document
      .querySelectorAll(
        ':is([handle="section4"], [handle="section5"], [handle="section6"], [handle="section7"]) .black-friday-2026-section4-products-viewport',
      )
      .forEach(updateSection5ProductControls);
    updateSection3TabOrientation();
  },
  { passive: true },
);
